import React, { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import CaldwellProjection from './Admin/CaldwellProjection';
import { adminReportsAPI } from '../services/api';
import { getPacificToday } from '../utils/dateUtils';
import './AdminReports.css';

interface FilterOptions {
  customers: Array<{ id: number; name: string }>;
  formulas: Array<{ id: number; name: string; code: string; animal_type: string }>;
  animal_types: Array<{ value: string; label: string }>;
}

interface DashboardSummary {
  total_orders: number;
  total_lbs: number;
  total_revenue: number;
  active_customers: number;
  avg_order_size: number;
  revenue_yoy_change: number;
  lbs_yoy_change: number;
}

interface MonthlySale {
  month: string;
  month_label: string;
  order_count: number;
  revenue: number;
  total_lbs: number;
  prev_year_revenue: number;
  prev_year_lbs: number;
}

interface TopCustomer {
  customer_id: number;
  customer_name: string;
  customer_type: string;
  order_count: number;
  total_revenue: number;
  total_lbs: number;
}

interface TopProduct {
  formula_id: number;
  formula_name: string;
  formula_code: string;
  animal_type: string;
  order_count: number;
  total_lbs: number;
  total_revenue: number;
}

interface AnimalBreakdown {
  animal_type: string;
  animal_label: string;
  total_lbs: number;
  total_revenue: number;
  order_count: number;
  percentage: number;
}

interface Forecasting {
  predictions: {
    next_month_revenue: number;
    next_month_lbs: number;
    monthly_avg_revenue: number;
    monthly_avg_lbs: number;
    monthly_avg_orders: number;
    trend_percentage: number;
  };
  inactive_customers: Array<{
    customer_id: number;
    customer_name: string;
    last_order_date: string;
    days_since_order: number;
  }>;
  inactive_customer_count: number;
}

interface QRPlacementPerformance {
  placement: string;
  label: string;
  scans: number;
  signups: number;
  conversion_rate: number;
  best_location: string | null;
}

interface QRStorePerformance {
  store_id: number | null;
  store_code: string;
  store_name: string;
  total_scans: number;
  floor_scans: number;
  register_scans: number;
  shelf_scans: number;
  bag_scans: number;
  signups: number;
  conversion_rate: number;
}

interface QRRecentScan {
  id: number;
  visited_at: string;
  store_code: string;
  store_name: string;
  placement: string;
  placement_label: string;
  device_type: string;
  converted: boolean;
  member_name: string | null;
}

interface QRAnalyticsData {
  summary: {
    total_scans: number;
    total_signups: number;
    overall_conversion_rate: number;
    scans_today: number;
    scans_this_week: number;
    scans_this_month: number;
    signups_this_month: number;
    week_over_week_trend: number;
    most_active_partner: string | null;
  };
  placement_performance: QRPlacementPerformance[];
  store_performance: QRStorePerformance[];
  daily_trend: Array<{ date: string; scans: number; signups: number }>;
  recent_scans: QRRecentScan[];
}

interface CustomerDetails {
  customer_id: number;
  customer_name: string;
  customer_type: string;
  city: string;
  state: string;
  order_count: number;
  total_revenue: number;
  total_lbs: number;
  avg_order_value: number;
  first_order: string | null;
  last_order: string | null;
}


// ── Member purchase history ─────────────────────────────────────────────────
interface MemberPurchaseRow {
  member_id: number;
  name: string;
  member_number: string | null;
  email: string;
  store: string | null;
  transaction_count: number;
  first_purchase: string | null;
  last_purchase: string | null;
  total_spend: number;
  total_lbs: number;
  avg_ticket: number;
  // null for a member with a single purchase — there is no interval between
  // purchases, and 0 would read as "came back the same day".
  median_days_between: number | null;
  days_since_last: number | null;
  top_products: string;
}

interface MemberPurchaseSummaryBlock {
  members: number;
  transactions: number;
  total_spend: number;
  total_lbs: number;
  avg_ticket: number;
  members_1: number;
  members_2_plus: number;
  members_5_plus: number;
  members_10_plus: number;
  repeat_rate: number;
  median_days_between: number | null;
}

interface GhostDistribution {
  transactions: number;
  card_payments: number;
  unidentifiable: number;
  distinct_cards: number;
  cards_seen_once: number;
  cards_seen_twice: number;
  cards_seen_3_plus: number;
  repeat_cards: number;
}

interface MemberPurchaseResponse {
  members: MemberPurchaseRow[];
  summary: MemberPurchaseSummaryBlock;
  ghosts: GhostDistribution;
  date_range: { from: string; to: string };
  notes: { dating: string; range: string; scope: string };
  generated_at: string;
}

const AdminReports: React.FC = () => {
  // Filters
  const [dateFrom, setDateFrom] = useState(() => {
    const today = getPacificToday();
    return `${today.slice(0, 4)}-01-01`; // January 1st of current year
  });
  const [dateTo, setDateTo] = useState(() => getPacificToday());
  const [customerId, setCustomerId] = useState<number | undefined>();
  const [filterOptions, setFilterOptions] = useState<FilterOptions | null>(null);

  // Data state
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [monthlySales, setMonthlySales] = useState<MonthlySale[]>([]);
  const [topCustomers, setTopCustomers] = useState<TopCustomer[]>([]);
  const [topProducts, setTopProducts] = useState<TopProduct[]>([]);
  const [animalBreakdown, setAnimalBreakdown] = useState<AnimalBreakdown[]>([]);
  const [forecasting, setForecasting] = useState<Forecasting | null>(null);
  const [customerDetails, setCustomerDetails] = useState<CustomerDetails[]>([]);
  const [qrAnalytics, setQRAnalytics] = useState<QRAnalyticsData | null>(null);
  const [qrLoading, setQRLoading] = useState(false);

  // New tab data
  const [channelDashboard, setChannelDashboard] = useState<any>(null);
  const [taxSummary, setTaxSummary] = useState<any>(null);
  const [taxExemptLog, setTaxExemptLog] = useState<any>(null);
  const [paymentData, setPaymentData] = useState<any>(null);
  const [posSummary, setPOSSummary] = useState<any>(null);
  const [feedLicense, setFeedLicense] = useState<any>(null);
  const [feedLicenseError, setFeedLicenseError] = useState<string | null>(null);
  const [salesDetail, setSalesDetail] = useState<any>(null);
  const [expandedSale, setExpandedSale] = useState<string | null>(null);
  const [categorySales, setCategorySales] = useState<any>(null);
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
  // Third level: a product opens to show its pack-tier and buyer breakdown.
  // Keyed "category|product" so two categories can hold the same product name.
  const [expandedProducts, setExpandedProducts] = useState<Record<string, boolean>>({});
  const [catChannelFilter, setCatChannelFilter] = useState('');

  // Feed license period
  const [licenseHalf, setLicenseHalf] = useState('1');
  const [licenseYear, setLicenseYear] = useState(new Date().getFullYear());
  const [feedLicenseExpanded, setFeedLicenseExpanded] = useState<Record<string, boolean>>({
    feedLine: true, animalGroup: true, stateShipments: true,
  });
  // Per-animal-group ration drill-down (collapsed by default).
  const [expandedAnimalGroups, setExpandedAnimalGroups] = useState<Record<string, boolean>>({});
  const [selectedState, setSelectedState] = useState<string>('');

  // Sales detail filters
  const [channelFilter, setChannelFilter] = useState('');
  const [sdTaxExempt, setSdTaxExempt] = useState('');
  const [sdSearch, setSdSearch] = useState('');

  // Tax report presets
  const [taxPreset, setTaxPreset] = useState('this_month');

  // UI state
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'customers' | 'products' | 'category-sales' | 'forecasting' | 'qr-analytics' | 'tax' | 'payments' | 'pos' | 'feed-license' | 'sales-detail' | 'promo-campaigns' | 'shipping' | 'discounts' | 'members' | 'caldwell'>('overview');

  // New report tab data
  const [promoCampaignReport, setPromoCampaignReport] = useState<any>(null);
  const [shippingReport, setShippingReport] = useState<any>(null);
  const [discountReport, setDiscountReport] = useState<any>(null);
  const [newTabLoading, setNewTabLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  // Member purchase history. memberSearch is its OWN box, sent to the server -
  // filtering a truncated page in the browser would silently hide members.
  const [memberData, setMemberData] = useState<MemberPurchaseResponse | null>(null);
  const [memberLoading, setMemberLoading] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [memberSort, setMemberSort] = useState('total_spend');
  const [memberDir, setMemberDir] = useState<'asc' | 'desc'>('desc');
  const [searchResults, setSearchResults] = useState<any[]>([]);

  // Load filter options on mount
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const response = await adminReportsAPI.getFilterOptions();
        setFilterOptions(response.data);
      } catch (error) {
        console.error('Error loading filter options:', error);
      }
    };
    loadFilterOptions();
  }, []);

  // Load data when filters change
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        date_from: dateFrom,
        date_to: dateTo,
        customer_id: customerId,
      };

      const [
        dashboardRes,
        monthlyRes,
        customersRes,
        productsRes,
        animalRes,
        forecastRes,
        detailsRes,
      ] = await Promise.all([
        adminReportsAPI.getDashboard(params),
        adminReportsAPI.getMonthlySales(params),
        adminReportsAPI.getTopCustomers({ date_from: dateFrom, date_to: dateTo, limit: 10 }),
        adminReportsAPI.getTopProducts({ ...params, limit: 10 }),
        adminReportsAPI.getAnimalBreakdown(params),
        adminReportsAPI.getForecasting(),
        adminReportsAPI.getCustomerBreakdown({ date_from: dateFrom, date_to: dateTo }),
      ]);

      setSummary(dashboardRes.data.summary);
      setMonthlySales(monthlyRes.data.monthly_sales);
      setTopCustomers(customersRes.data.top_customers);
      setTopProducts(productsRes.data.top_products);
      setAnimalBreakdown(animalRes.data.animal_breakdown);
      setForecasting(forecastRes.data);
      setCustomerDetails(detailsRes.data.customers);
    } catch (error) {
      console.error('Error loading report data:', error);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, customerId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Load QR analytics when tab is selected
  useEffect(() => {
    if (activeTab === 'qr-analytics' && !qrAnalytics && !qrLoading) {
      loadQRAnalytics();
    }
  }, [activeTab]);

  const loadQRAnalytics = async () => {
    setQRLoading(true);
    try {
      const response = await adminReportsAPI.getQRAnalytics();
      setQRAnalytics(response.data);
    } catch (error) {
      console.error('Error loading QR analytics:', error);
    } finally {
      setQRLoading(false);
    }
  };

  // Member purchase history. Its own effect because it re-fetches on SEARCH and
  // SORT as well as the date range - the other tabs only depend on the range,
  // and folding this into their effect would refetch all of them on every
  // keystroke.
  useEffect(() => {
    if (activeTab !== 'members') return;
    let alive = true;
    setMemberLoading(true);
    adminReportsAPI.getMemberPurchaseSummary({
      date_from: dateFrom,
      date_to: dateTo,
      search: memberSearch || undefined,
      sort: memberSort,
      dir: memberDir,
    })
      .then(r => { if (alive) setMemberData(r.data); })
      .catch(err => { if (alive) { console.error(err); setMemberData(null); } })
      .finally(() => { if (alive) setMemberLoading(false); });
    // Out-of-order responses cannot win: the cleanup flips `alive` the moment
    // any input changes, so a slow reply for an older search is disowned.
    return () => { alive = false; };
  }, [activeTab, dateFrom, dateTo, memberSearch, memberSort, memberDir]);

  // Load tab-specific data on demand
  useEffect(() => {
    const params = { date_from: dateFrom, date_to: dateTo };

    if (activeTab === 'overview' && !channelDashboard) {
      adminReportsAPI.getAllChannelDashboard(params).then(r => setChannelDashboard(r.data)).catch(console.error);
    }
    if (activeTab === 'tax' && !taxSummary) {
      Promise.all([
        adminReportsAPI.getTaxSummary(params),
        adminReportsAPI.getTaxExemptLog(params),
      ]).then(([ts, tel]) => {
        setTaxSummary(ts.data);
        setTaxExemptLog(tel.data);
      }).catch(console.error);
    }
    if (activeTab === 'payments' && !paymentData) {
      adminReportsAPI.getPaymentBreakdown(params).then(r => setPaymentData(r.data)).catch(console.error);
    }
    if (activeTab === 'pos' && !posSummary) {
      adminReportsAPI.getPOSSummary(params).then(r => setPOSSummary(r.data)).catch(console.error);
    }
    if (activeTab === 'sales-detail' && !salesDetail) {
      adminReportsAPI.getSalesDetail({ ...params, channel: channelFilter, tax_exempt: sdTaxExempt, search: sdSearch }).then(r => setSalesDetail(r.data)).catch(console.error);
    }
    // NOTE: category-sales is handled by its own effect below so it can
    // also react to date-range + channel changes (not just tab switches).
    if (activeTab === 'promo-campaigns' && !promoCampaignReport) {
      setNewTabLoading(true);
      adminReportsAPI.getPromoCampaignsReport(params).then(r => { setPromoCampaignReport(r.data); setNewTabLoading(false); }).catch(e => { console.error(e); setNewTabLoading(false); });
    }
    if (activeTab === 'shipping' && !shippingReport) {
      setNewTabLoading(true);
      adminReportsAPI.getShippingAnalysis(params).then(r => { setShippingReport(r.data); setNewTabLoading(false); }).catch(e => { console.error(e); setNewTabLoading(false); });
    }
    if (activeTab === 'discounts' && !discountReport) {
      setNewTabLoading(true);
      adminReportsAPI.getDiscountAnalysis(params).then(r => { setDiscountReport(r.data); setNewTabLoading(false); }).catch(e => { console.error(e); setNewTabLoading(false); });
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'feed-license') {
      setFeedLicense(null);
      setFeedLicenseError(null);
      adminReportsAPI.getFeedLicenseReport({ half: licenseHalf, year: licenseYear })
        .then(r => setFeedLicense(r.data))
        .catch(err => {
          console.error('Feed license report error:', err);
          setFeedLicenseError('Failed to load feed license report. Please try again.');
        });
    }
  }, [activeTab, licenseHalf, licenseYear]);

  // Reload channel-dependent data when filters change
  // (category-sales intentionally excluded — its own effect below
  //  refetches directly on date/channel change.)
  useEffect(() => {
    setChannelDashboard(null);
    setTaxSummary(null);
    setTaxExemptLog(null);
    setPaymentData(null);
    setPOSSummary(null);
    setSalesDetail(null);
    setPromoCampaignReport(null);
    setShippingReport(null);
    setDiscountReport(null);
  }, [dateFrom, dateTo]);

  // Category Sales: refetch whenever the tab is active AND the date
  // range or channel filter changes (not just on tab switch). Previously
  // the loader keyed on [activeTab] only and a separate effect just
  // nulled the data on date change without reloading — so changing the
  // page date picker left the tab showing its initial year-to-date load.
  useEffect(() => {
    if (activeTab !== 'category-sales') return;
    setCategorySales(null);
    adminReportsAPI.getCategorySales({
      date_from: dateFrom,
      date_to: dateTo,
      channel: catChannelFilter,
    })
      .then(r => setCategorySales(r.data))
      .catch(console.error);
  }, [activeTab, dateFrom, dateTo, catChannelFilter]);

  const loadSalesDetail = () => {
    adminReportsAPI.getSalesDetail({
      date_from: dateFrom, date_to: dateTo,
      channel: channelFilter, tax_exempt: sdTaxExempt, search: sdSearch,
    }).then(r => setSalesDetail(r.data)).catch(console.error);
  };

  const loadCategorySales = () => {
    setCategorySales(null);
    adminReportsAPI.getCategorySales({
      date_from: dateFrom, date_to: dateTo, channel: catChannelFilter,
    }).then(r => setCategorySales(r.data)).catch(console.error);
  };

  const applyTaxPreset = (preset: string) => {
    setTaxPreset(preset);
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    let from = '', to = '';
    switch (preset) {
      case 'this_month': from = `${y}-${String(m + 1).padStart(2, '0')}-01`; to = now.toISOString().split('T')[0]; break;
      case 'last_month': { const lm = m === 0 ? 11 : m - 1; const ly = m === 0 ? y - 1 : y; const ld = new Date(ly, lm + 1, 0).getDate(); from = `${ly}-${String(lm + 1).padStart(2, '0')}-01`; to = `${ly}-${String(lm + 1).padStart(2, '0')}-${ld}`; break; }
      case 'this_quarter': { const qs = Math.floor(m / 3) * 3; from = `${y}-${String(qs + 1).padStart(2, '0')}-01`; to = now.toISOString().split('T')[0]; break; }
      case 'last_quarter': { const lqs = (Math.floor(m / 3) - 1) * 3; const lqy = lqs < 0 ? y - 1 : y; const qs2 = lqs < 0 ? 9 : lqs; const qe = new Date(lqy, qs2 + 3, 0).getDate(); from = `${lqy}-${String(qs2 + 1).padStart(2, '0')}-01`; to = `${lqy}-${String(qs2 + 3).padStart(2, '0')}-${qe}`; break; }
      case 'jan_jun': from = `${y}-01-01`; to = `${y}-06-30`; break;
      case 'jul_dec': from = `${y}-07-01`; to = `${y}-12-31`; break;
      case 'this_year': from = `${y}-01-01`; to = now.toISOString().split('T')[0]; break;
    }
    if (from && to) {
      setDateFrom(from);
      setDateTo(to);
      setTaxSummary(null);
      setTaxExemptLog(null);
    }
  };

  const CHANNEL_COLORS = ['#508590', '#E73C3E', '#C3AD8B', '#3F2021'];
  const PAYMENT_COLORS = ['#2e7d32', '#1565c0', '#00838f', '#6a1b9a', '#e65100'];

  // Search orders
  const handleSearch = async () => {
    if (searchQuery.length < 2) return;
    try {
      const response = await adminReportsAPI.searchOrders(searchQuery);
      setSearchResults(response.data.orders);
    } catch (error) {
      console.error('Error searching orders:', error);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const paymentMethodLabel = (key: string): string => {
    const labels: Record<string, string> = {
      cash: 'Cash', credit: 'Credit', debit: 'Debit', check: 'Check',
      invoice: 'Invoice', other: 'Other', petty_cash_out: 'Petty Cash Out',
    };
    return labels[key] || key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ');
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(Math.round(value));
  };

  // Pounds, with a trailing + when the backend could not establish the weight
  // of every line in the group (a product with no bag size on file, or a
  // wholesale line with no pounds recorded). The figure is then a floor, and
  // saying so is the difference between a number and a guess.
  const formatLbs = (value: number, partial?: boolean) => (
    <span title={partial
      ? 'At least one line has no recorded weight, so the true total is higher than this.'
      : undefined}>
      {formatNumber(value)}{partial ? '+' : ''}
    </span>
  );

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Calculate max values for bar charts
  const maxRevenue = Math.max(...monthlySales.map(m => Math.max(m.revenue, m.prev_year_revenue)), 1);
  const maxCustomerRevenue = Math.max(...topCustomers.map(c => c.total_revenue), 1);
  const maxProductLbs = Math.max(...topProducts.map(p => p.total_lbs), 1);

  const handleExportCSV = (
    type: 'orders' | 'customers' | 'products' | 'member_purchases'
      | `caldwell_${string}`,
  ) => {
    const url = adminReportsAPI.exportCSV({
      type,
      date_from: dateFrom,
      date_to: dateTo,
      customer_id: customerId,
      // The member export honours the search box, so the file matches the
      // population on screen rather than quietly exporting everyone.
      ...(type === 'member_purchases' && memberSearch
        ? { search: memberSearch }
        : {}),
    });
    window.open(url, '_blank');
  };

  if (loading && !summary) {
    return (
      <div className="admin-reports-loading">
        <div className="spinner"></div>
        <p>Loading reports...</p>
      </div>
    );
  }

  return (
    <div className="admin-reports">
      <div className="admin-reports-header">
        <h1>Business Analytics & Reports</h1>
        <p className="admin-reports-subtitle">Comprehensive sales data and customer insights</p>
      </div>

      {/* Filters */}
      <div className="admin-reports-filters">
        <div className="filter-group">
          <label>Date Range</label>
          <div className="date-range">
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
            <span>to</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>
        </div>
        <div className="filter-group">
          <label>Customer</label>
          <select
            value={customerId || ''}
            onChange={(e) => setCustomerId(e.target.value ? parseInt(e.target.value) : undefined)}
          >
            <option value="">All Customers</option>
            {filterOptions?.customers.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div className="filter-group search-group">
          <label>Search Orders</label>
          <div className="search-input-wrapper">
            <input
              type="text"
              placeholder="Order number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
            <button onClick={handleSearch} className="btn-search">Search</button>
          </div>
        </div>
      </div>

      {/* Search Results */}
      {searchResults.length > 0 && (
        <div className="search-results-panel">
          <h3>Search Results</h3>
          <button className="btn-close" onClick={() => setSearchResults([])}>Close</button>
          <table className="search-results-table">
            <thead>
              <tr>
                <th>Order #</th>
                <th>Customer</th>
                <th>Date</th>
                <th>Status</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {searchResults.map((order) => (
                <tr key={order.id}>
                  <td>{order.order_number}</td>
                  <td>{order.customer_name}</td>
                  <td>{formatDate(order.order_date)}</td>
                  <td><span className={`status-badge ${order.status.toLowerCase()}`}>{order.status_display}</span></td>
                  <td>{formatCurrency(order.total_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Tabs */}
      <div className="admin-reports-tabs">
        <button
          className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={`tab-btn ${activeTab === 'customers' ? 'active' : ''}`}
          onClick={() => setActiveTab('customers')}
        >
          Customers
        </button>
        <button
          className={`tab-btn ${activeTab === 'products' ? 'active' : ''}`}
          onClick={() => setActiveTab('products')}
        >
          Products
        </button>
        <button
          className={`tab-btn ${activeTab === 'category-sales' ? 'active' : ''}`}
          onClick={() => setActiveTab('category-sales')}
        >
          Category Sales
        </button>
        <button
          className={`tab-btn ${activeTab === 'forecasting' ? 'active' : ''}`}
          onClick={() => setActiveTab('forecasting')}
        >
          Forecasting
        </button>
        <button
          className={`tab-btn ${activeTab === 'qr-analytics' ? 'active' : ''}`}
          onClick={() => setActiveTab('qr-analytics')}
        >
          QR Analytics
        </button>
        <button
          className={`tab-btn ${activeTab === 'tax' ? 'active' : ''}`}
          onClick={() => setActiveTab('tax')}
        >
          Tax Report
        </button>
        <button
          className={`tab-btn ${activeTab === 'payments' ? 'active' : ''}`}
          onClick={() => setActiveTab('payments')}
        >
          Payments & AR
        </button>
        <button
          className={`tab-btn ${activeTab === 'pos' ? 'active' : ''}`}
          onClick={() => setActiveTab('pos')}
        >
          POS Report
        </button>
        <button
          className={`tab-btn ${activeTab === 'feed-license' ? 'active' : ''}`}
          onClick={() => setActiveTab('feed-license')}
        >
          Feed License
        </button>
        <button
          className={`tab-btn ${activeTab === 'sales-detail' ? 'active' : ''}`}
          onClick={() => setActiveTab('sales-detail')}
        >
          Sales Detail
        </button>
        <button
          className={`tab-btn ${activeTab === 'promo-campaigns' ? 'active' : ''}`}
          onClick={() => setActiveTab('promo-campaigns')}
        >
          Promo Campaigns
        </button>
        <button
          className={`tab-btn ${activeTab === 'shipping' ? 'active' : ''}`}
          onClick={() => setActiveTab('shipping')}
        >
          Shipping
        </button>
        <button
          className={`tab-btn ${activeTab === 'discounts' ? 'active' : ''}`}
          onClick={() => setActiveTab('discounts')}
        >
          Discounts
        </button>
        <button
          className={`tab-btn ${activeTab === 'members' ? 'active' : ''}`}
          onClick={() => setActiveTab('members')}
        >
          Member Purchases
        </button>
        <button
          className={`tab-btn ${activeTab === 'caldwell' ? 'active' : ''}`}
          onClick={() => setActiveTab('caldwell')}
        >
          Caldwell Projection
        </button>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="tab-content">
          {/* Summary Cards */}
          {summary && (
            <div className="summary-cards">
              <div className="summary-card">
                <div className="card-icon orders-icon">
                  <span>Orders</span>
                </div>
                <div className="card-content">
                  <div className="card-value">{formatNumber(summary.total_orders)}</div>
                  <div className="card-label">Total Orders</div>
                </div>
              </div>
              <div className="summary-card">
                <div className="card-icon lbs-icon">
                  <span>Lbs</span>
                </div>
                <div className="card-content">
                  <div className="card-value">{formatNumber(summary.total_lbs)}</div>
                  <div className="card-label">Total Pounds</div>
                  <div className={`card-change ${summary.lbs_yoy_change >= 0 ? 'positive' : 'negative'}`}>
                    {summary.lbs_yoy_change >= 0 ? '+' : ''}{summary.lbs_yoy_change.toFixed(1)}% YoY
                  </div>
                </div>
              </div>
              <div className="summary-card">
                <div className="card-icon revenue-icon">
                  <span>$</span>
                </div>
                <div className="card-content">
                  <div className="card-value">{formatCurrency(summary.total_revenue)}</div>
                  <div className="card-label">Total Revenue</div>
                  <div className={`card-change ${summary.revenue_yoy_change >= 0 ? 'positive' : 'negative'}`}>
                    {summary.revenue_yoy_change >= 0 ? '+' : ''}{summary.revenue_yoy_change.toFixed(1)}% YoY
                  </div>
                </div>
              </div>
              <div className="summary-card">
                <div className="card-icon customers-icon">
                  <span>Cust</span>
                </div>
                <div className="card-content">
                  <div className="card-value">{formatNumber(summary.active_customers)}</div>
                  <div className="card-label">Active Customers</div>
                </div>
              </div>
              <div className="summary-card">
                <div className="card-icon avg-icon">
                  <span>Avg</span>
                </div>
                <div className="card-content">
                  <div className="card-value">{formatCurrency(summary.avg_order_size)}</div>
                  <div className="card-label">Avg Order Size</div>
                </div>
              </div>
            </div>
          )}

          {/* Channel Revenue Breakdown */}
          {channelDashboard && (
            <div className="charts-row" style={{ marginBottom: '0' }}>
              <div className="chart-card">
                <h3>Revenue by Channel</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={channelDashboard.channel_breakdown.filter((c: any) => c.revenue > 0).map((c: any) => ({
                        name: c.channel === 'pos' ? 'POS' : c.channel === 'wholesale' ? 'Wholesale' : c.channel === 'bridge_club' ? 'Bridge Club' : 'Retail Partners',
                        value: c.revenue,
                      }))}
                      cx="50%" cy="50%" outerRadius={80} dataKey="value"
                      label={(props: any) => `${props.name || ''} ${((props.percent || 0) * 100).toFixed(0)}%`}
                    >
                      {channelDashboard.channel_breakdown.filter((c: any) => c.revenue > 0).map((_: any, i: number) => (
                        <Cell key={i} fill={CHANNEL_COLORS[i % CHANNEL_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value: number) => formatCurrency(value)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="chart-card">
                <h3>Payment Breakdown (POS)</h3>
                <div style={{ padding: '0.5rem' }}>
                  {Object.entries(channelDashboard.payment_breakdown as Record<string, number>).filter(([, v]) => v !== 0).map(([method, amount]) => (
                    <div key={method} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #eee' }}>
                      <span style={amount < 0 ? { color: '#d32f2f' } : undefined}>{paymentMethodLabel(method)}</span>
                      <span style={{ fontWeight: 'bold', color: amount < 0 ? '#d32f2f' : undefined }}>{formatCurrency(amount)}</span>
                    </div>
                  ))}
                  {channelDashboard.totals.total_surcharges > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', color: '#e65100' }}>
                      <span>CC Surcharges</span>
                      <span style={{ fontWeight: 'bold' }}>{formatCurrency(channelDashboard.totals.total_surcharges)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Charts Row */}
          <div className="charts-row">
            {/* Monthly Sales Chart */}
            <div className="chart-card wide">
              <div className="chart-header">
                <h3>Monthly Sales</h3>
                <div className="chart-legend">
                  <span className="legend-item current">Current Year</span>
                  <span className="legend-item previous">Previous Year</span>
                </div>
              </div>
              <div className="bar-chart monthly-chart">
                {monthlySales.map((month) => (
                  <div key={month.month} className="chart-bar-group">
                    <div className="bar-container">
                      <div
                        className="bar current"
                        style={{ height: `${(month.revenue / maxRevenue) * 100}%` }}
                        title={`${month.month_label}: ${formatCurrency(month.revenue)}`}
                      ></div>
                      <div
                        className="bar previous"
                        style={{ height: `${(month.prev_year_revenue / maxRevenue) * 100}%` }}
                        title={`Previous Year: ${formatCurrency(month.prev_year_revenue)}`}
                      ></div>
                    </div>
                    <span className="bar-label">{month.month_label.split(' ')[0]}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Animal Type Breakdown */}
            <div className="chart-card">
              <h3>Sales by Animal Type</h3>
              <div className="pie-chart-container">
                {animalBreakdown.map((animal, index) => (
                  <div key={animal.animal_type} className="animal-row">
                    <div className="animal-info">
                      <span
                        className="animal-color"
                        style={{ backgroundColor: getAnimalColor(index) }}
                      ></span>
                      <span className="animal-name">{animal.animal_label}</span>
                    </div>
                    <div className="animal-stats">
                      <span className="animal-percentage">{animal.percentage}%</span>
                      <span className="animal-lbs">{formatNumber(animal.total_lbs)} lbs</span>
                    </div>
                    <div className="animal-bar-bg">
                      <div
                        className="animal-bar-fill"
                        style={{
                          width: `${animal.percentage}%`,
                          backgroundColor: getAnimalColor(index),
                        }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Top Charts Row */}
          <div className="charts-row">
            {/* Top Customers */}
            <div className="chart-card">
              <div className="chart-header">
                <h3>Top 10 Wholesale Customers</h3>
                <button
                  className="btn-export-small"
                  onClick={() => handleExportCSV('customers')}
                >
                  Export CSV
                </button>
              </div>
              <div className="horizontal-bar-chart">
                {topCustomers.map((customer, index) => (
                  <div key={customer.customer_id} className="h-bar-row">
                    <div className="h-bar-rank">{index + 1}</div>
                    <div className="h-bar-name" title={customer.customer_name}>
                      {customer.customer_name}
                    </div>
                    <div className="h-bar-wrapper">
                      <div
                        className="h-bar-fill"
                        style={{ width: `${(customer.total_revenue / maxCustomerRevenue) * 100}%` }}
                      ></div>
                    </div>
                    <div className="h-bar-value">{formatCurrency(customer.total_revenue)}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Top Products */}
            <div className="chart-card">
              <div className="chart-header">
                <h3>Top 10 Products (by Volume)</h3>
                <button
                  className="btn-export-small"
                  onClick={() => handleExportCSV('products')}
                >
                  Export CSV
                </button>
              </div>
              <div className="horizontal-bar-chart">
                {topProducts.map((product, index) => (
                  <div key={product.formula_id} className="h-bar-row">
                    <div className="h-bar-rank">{index + 1}</div>
                    <div className="h-bar-name" title={product.formula_name}>
                      {product.formula_name}
                    </div>
                    <div className="h-bar-wrapper">
                      <div
                        className="h-bar-fill product"
                        style={{ width: `${(product.total_lbs / maxProductLbs) * 100}%` }}
                      ></div>
                    </div>
                    <div className="h-bar-value">{formatNumber(product.total_lbs)} lbs</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Customers Tab */}
      {activeTab === 'customers' && (
        <div className="tab-content">
          <div className="table-header">
            <h3>Customer Breakdown</h3>
            <button
              className="btn-export"
              onClick={() => handleExportCSV('customers')}
            >
              Export to CSV
            </button>
          </div>
          <div className="data-table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Type</th>
                  <th>Location</th>
                  <th>Orders</th>
                  <th>Total Lbs</th>
                  <th>Total Revenue</th>
                  <th>Avg Order</th>
                  <th>Last Order</th>
                </tr>
              </thead>
              <tbody>
                {customerDetails.map((customer) => (
                  <tr key={customer.customer_id}>
                    <td className="customer-name-cell">{customer.customer_name}</td>
                    <td><span className="type-badge">{customer.customer_type}</span></td>
                    <td>{customer.city}, {customer.state}</td>
                    <td className="number-cell">{customer.order_count}</td>
                    <td className="number-cell">{formatNumber(customer.total_lbs)}</td>
                    <td className="number-cell">{formatCurrency(customer.total_revenue)}</td>
                    <td className="number-cell">{formatCurrency(customer.avg_order_value)}</td>
                    <td>{customer.last_order ? formatDate(customer.last_order) : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}


      {/* Caldwell Projection Tab. The section body lives in its own component
          so this file carries only the tab wiring - it is already the largest
          page in the app and two sessions keep meeting in it. */}
      {activeTab === 'caldwell' && (
        <div className="tab-content">
          <CaldwellProjection
            dateFrom={dateFrom}
            dateTo={dateTo}
            onExport={(section) => handleExportCSV(`caldwell_${section}`)}
          />
        </div>
      )}

      {/* Member Purchases Tab */}
      {activeTab === 'members' && (
        <div className="tab-content">
          {/* THE CAVEATS BELONG ON THE PAGE, not in a conversation. This report
              and the Store Ledger will be reconciled against each other, and the
              reader needs to know why the counts differ before they find it. */}
          <div className="member-notes">
            <p>{memberData?.notes.range ??
              'Member attribution began April 2026. Reports default to May 1, 2026, the first full month of complete attribution. Earlier transactions exist but cannot be attributed to members.'}</p>
            <p>{memberData?.notes.dating ??
              'Dated by completion, matching the Store Ledger. The Overview tab dates POS by start time, so boundary transactions may differ by one day.'}</p>
            <p>{memberData?.notes.scope ??
              'Wholesale orders are excluded: a SalesOrder line has no path to a Bridge Club member, so it cannot be attributed to one.'}</p>
          </div>

          <div className="table-header">
            <h3>Member Purchases</h3>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="text"
                className="member-search"
                placeholder="Name, email or member number…"
                value={memberSearch}
                onChange={(e) => setMemberSearch(e.target.value)}
              />
              <button
                className="btn-export"
                onClick={() => handleExportCSV('member_purchases')}
              >
                Export to CSV
              </button>
            </div>
          </div>

          {memberLoading && <p>Loading…</p>}

          {!memberLoading && memberData && memberData.members.length === 0 && (
            <p className="empty-state">No data in range.</p>
          )}

          {!memberLoading && memberData && memberData.members.length > 0 && (
            <>
              <div className="summary-cards">
                <div className="summary-card">
                  <div className="card-label">Members</div>
                  <div className="card-value">{formatNumber(memberData.summary.members)}</div>
                </div>
                <div className="summary-card">
                  <div className="card-label">Transactions</div>
                  <div className="card-value">{formatNumber(memberData.summary.transactions)}</div>
                </div>
                <div className="summary-card">
                  <div className="card-label">Total Spend</div>
                  <div className="card-value">{formatCurrency(memberData.summary.total_spend)}</div>
                </div>
                <div className="summary-card">
                  <div className="card-label">Average Ticket</div>
                  <div className="card-value">{formatCurrency(memberData.summary.avg_ticket)}</div>
                </div>
                <div className="summary-card">
                  <div className="card-label">Repeat Rate</div>
                  <div className="card-value">
                    {(memberData.summary.repeat_rate * 100).toFixed(1)}%
                  </div>
                  <div className="card-sub">
                    {memberData.summary.members_2_plus} of {memberData.summary.members} came back
                  </div>
                </div>
                <div className="summary-card">
                  <div className="card-label">Median Days Between</div>
                  <div className="card-value">
                    {memberData.summary.median_days_between === null
                      ? '—'
                      : memberData.summary.median_days_between.toFixed(0)}
                  </div>
                </div>
              </div>

              <div className="member-buckets">
                <span>1 purchase: <strong>{memberData.summary.members_1}</strong></span>
                <span>2+: <strong>{memberData.summary.members_2_plus}</strong></span>
                <span>5+: <strong>{memberData.summary.members_5_plus}</strong></span>
                <span>10+: <strong>{memberData.summary.members_10_plus}</strong></span>
              </div>

              <div className="data-table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      {([
                        ['name', 'Member'],
                        ['transaction_count', 'Visits'],
                        ['total_spend', 'Total Spend'],
                        ['avg_ticket', 'Avg Ticket'],
                        ['total_lbs', 'Total Lbs'],
                        ['median_days_between', 'Median Days'],
                        ['days_since_last', 'Days Since'],
                        ['first_purchase', 'First'],
                        ['last_purchase', 'Last'],
                      ] as [string, string][]).map(([key, label]) => (
                        <th
                          key={key}
                          className="sortable"
                          onClick={() => {
                            // Sorting is SERVER-SIDE - it reorders the whole
                            // filtered set, not the rows already on screen.
                            if (memberSort === key) {
                              setMemberDir(memberDir === 'desc' ? 'asc' : 'desc');
                            } else {
                              setMemberSort(key);
                              setMemberDir('desc');
                            }
                          }}
                        >
                          {label}
                          {memberSort === key && (memberDir === 'desc' ? ' ▼' : ' ▲')}
                        </th>
                      ))}
                      <th>Top Products</th>
                    </tr>
                  </thead>
                  <tbody>
                    {memberData.members.map((m) => (
                      <tr key={m.member_id}>
                        <td className="customer-name-cell">
                          {m.name}
                          <div className="member-sub">
                            {m.member_number || '—'} · {m.email}
                          </div>
                        </td>
                        <td className="number-cell">{m.transaction_count}</td>
                        <td className="number-cell">{formatCurrency(m.total_spend)}</td>
                        <td className="number-cell">{formatCurrency(m.avg_ticket)}</td>
                        <td className="number-cell">{formatNumber(m.total_lbs)}</td>
                        {/* An em dash, not 0 - a member with one purchase has no
                            interval between purchases at all. */}
                        <td className="number-cell">
                          {m.median_days_between === null
                            ? '—'
                            : m.median_days_between.toFixed(0)}
                        </td>
                        <td className="number-cell">
                          {m.days_since_last === null ? '—' : m.days_since_last}
                        </td>
                        <td>{m.first_purchase ? formatDate(m.first_purchase) : '—'}</td>
                        <td>{m.last_purchase ? formatDate(m.last_purchase) : '—'}</td>
                        <td className="top-products-cell">{m.top_products || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* GHOST CUSTOMERS — inferred, and kept visibly apart from the member
              figures above. Counts only for now: whether this deserves a full
              table depends on how many cards actually repeat. */}
          {!memberLoading && memberData && (
            <div className="ghost-section">
              <h3>Anonymous card customers (inferred)</h3>
              <p className="ghost-caveat">
                Unattached card sales grouped by the card's last four digits. A last
                four is <strong>not a unique identifier</strong> — ten thousand
                combinations against a few hundred cards collide occasionally, and a
                reissued card gets new digits. Each of these is a <em>probable</em>
                customer, not a confirmed one.
              </p>
              <p className="ghost-caveat">
                These figures are inferred and are <strong>excluded</strong> from the
                member counts and the repeat rate above.
              </p>
              <div className="member-buckets">
                <span>Transactions: <strong>{memberData.ghosts.transactions}</strong></span>
                <span>Distinct cards: <strong>{memberData.ghosts.distinct_cards}</strong></span>
                <span>Seen once: <strong>{memberData.ghosts.cards_seen_once}</strong></span>
                <span>Twice: <strong>{memberData.ghosts.cards_seen_twice}</strong></span>
                <span>3+: <strong>{memberData.ghosts.cards_seen_3_plus}</strong></span>
                <span>
                  No last four: <strong>{memberData.ghosts.unidentifiable}</strong>
                </span>
              </div>
              {memberData.ghosts.unidentifiable > 0 && (
                <p className="ghost-caveat">
                  {memberData.ghosts.unidentifiable} card payment
                  {memberData.ghosts.unidentifiable === 1 ? '' : 's'} carried no last
                  four and cannot be grouped at all.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Products Tab */}
      {activeTab === 'products' && (
        <div className="tab-content">
          <div className="table-header">
            <h3>Product Breakdown</h3>
            <button
              className="btn-export"
              onClick={() => handleExportCSV('products')}
            >
              Export to CSV
            </button>
          </div>
          <div className="data-table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Code</th>
                  <th>Animal Type</th>
                  <th>Orders</th>
                  <th>Customers</th>
                  <th>Total Lbs</th>
                  <th>Total Revenue</th>
                </tr>
              </thead>
              <tbody>
                {topProducts.map((product) => (
                  <tr key={product.formula_id}>
                    <td className="product-name-cell">{product.formula_name}</td>
                    <td>{product.formula_code}</td>
                    <td>{product.animal_type}</td>
                    <td className="number-cell">{product.order_count}</td>
                    <td className="number-cell">-</td>
                    <td className="number-cell">{formatNumber(product.total_lbs)}</td>
                    <td className="number-cell">{formatCurrency(product.total_revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Category Sales Tab */}
      {activeTab === 'category-sales' && (
        <div className="tab-content">
          <div className="table-header">
            <h3>Category Sales Breakdown</h3>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <select value={catChannelFilter} onChange={e => setCatChannelFilter(e.target.value)}
                style={{ padding: '0.4rem 0.6rem', borderRadius: '6px', border: '1px solid #ddd' }}>
                <option value="">All Channels</option>
                <option value="pos">POS</option>
                <option value="wholesale">Wholesale</option>
                <option value="bridge_club">Bridge / Store Club</option>
              </select>
              <button className="btn-export" onClick={loadCategorySales}>Apply</button>
              <button className="btn-export" onClick={() => {
                const params: Record<string, string> = { date_from: dateFrom, date_to: dateTo };
                if (catChannelFilter) params.channel = catChannelFilter;
                window.open(adminReportsAPI.exportReportCSV('category_sales', params), '_blank');
              }}>Export CSV</button>
            </div>
          </div>

          {!categorySales && <div style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>Loading...</div>}

          {categorySales && categorySales.categories.length === 0 && (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>No sales data for this period.</div>
          )}

          {categorySales && categorySales.excluded && categorySales.excluded.length > 0 && (
            <div style={{ fontSize: '0.85rem', color: '#666', margin: '0 0 0.75rem' }}>
              Not counted as till sales:{' '}
              {categorySales.excluded.map((e: any, i: number) => (
                <span key={e.key}>{i > 0 ? '; ' : ''}{e.label} — {e.lines} lines, {formatCurrency(e.amount)}</span>
              ))}
            </div>
          )}

          {categorySales && categorySales.categories.length > 0 && (
            <>
              <div className="summary-cards">
                <div className="summary-card">
                  <div className="summary-label">Pre-Tax Sales</div>
                  <div className="summary-value">{formatCurrency(categorySales.totals.pre_tax)}</div>
                </div>
                <div className="summary-card">
                  <div className="summary-label">Tax Collected</div>
                  <div className="summary-value">{formatCurrency(categorySales.totals.tax)}</div>
                </div>
                <div className="summary-card">
                  <div className="summary-label">Post-Tax Total</div>
                  <div className="summary-value">{formatCurrency(categorySales.totals.post_tax)}</div>
                </div>
                <div className="summary-card">
                  <div className="summary-label">Transactions</div>
                  <div className="summary-value">{formatNumber(categorySales.totals.transactions)}</div>
                </div>
              </div>

              <div className="chart-card" style={{ marginTop: '1rem' }}>
                <div className="data-table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th style={{ width: '30px' }}></th>
                        <th>Category</th>
                        <th className="number-cell">Pre-Tax</th>
                        <th className="number-cell">Tax</th>
                        <th className="number-cell">Post-Tax</th>
                        <th className="number-cell" title="Countable things sold: bags, jugs, each. A bulk fill is weighed, not counted, so it adds pounds but no units.">Units</th>
                        <th className="number-cell" title="Pounds moved. Bulk by scale weight; bags by bag size.">Lbs</th>
                        <th className="number-cell">Txns</th>
                        <th className="number-cell">% of Sales</th>
                      </tr>
                    </thead>
                    <tbody>
                      {categorySales.categories.map((cat: any) => (
                        <React.Fragment key={cat.name}>
                          <tr className="category-row" style={{ cursor: 'pointer' }}
                            onClick={() => setExpandedCategories(prev => ({ ...prev, [cat.name]: !prev[cat.name] }))}>
                            <td className="category-arrow">{expandedCategories[cat.name] ? '▼' : '▶'}</td>
                            <td><strong>{cat.name}</strong></td>
                            <td className="number-cell">{formatCurrency(cat.pre_tax)}</td>
                            <td className="number-cell">{formatCurrency(cat.tax)}</td>
                            <td className="number-cell"><strong>{formatCurrency(cat.post_tax)}</strong></td>
                            <td className="number-cell">{formatNumber(cat.units)}</td>
                            <td className="number-cell">{formatLbs(cat.lbs, cat.lbs_partial)}</td>
                            <td className="number-cell">{cat.transactions}</td>
                            <td className="number-cell">{cat.pct_of_sales.toFixed(1)}%</td>
                          </tr>
                          {expandedCategories[cat.name] && cat.products.map((p: any) => {
                            const pkey = `${cat.name}|${p.name}`;
                            const breakdowns: Array<[string, any[]]> = [
                              ['Pack', p.by_pack || []],
                              ['Sold to', p.by_buyer || []],
                            ];
                            const hasDetail = breakdowns.some(([, rows]) => rows.length > 0);
                            const open = !!expandedProducts[pkey];
                            return (
                              <React.Fragment key={pkey}>
                                <tr
                                  style={{ backgroundColor: '#fafafa', cursor: hasDetail ? 'pointer' : 'default' }}
                                  onClick={hasDetail
                                    ? () => setExpandedProducts(prev => ({ ...prev, [pkey]: !prev[pkey] }))
                                    : undefined}
                                >
                                  <td></td>
                                  <td style={{ paddingLeft: '2rem', color: '#666' }}>
                                    {hasDetail && (
                                      <span style={{ color: '#999', marginRight: '0.4rem' }}>
                                        {open ? '▾' : '▸'}
                                      </span>
                                    )}
                                    {p.name}
                                  </td>
                                  <td className="number-cell" style={{ color: '#666' }}>{formatCurrency(p.pre_tax)}</td>
                                  <td className="number-cell" style={{ color: '#666' }}>{formatCurrency(p.tax)}</td>
                                  <td className="number-cell" style={{ color: '#666' }}>{formatCurrency(p.post_tax)}</td>
                                  <td className="number-cell" style={{ color: '#666' }}>{formatNumber(p.units)}</td>
                                  <td className="number-cell" style={{ color: '#666' }}>{formatLbs(p.lbs, p.lbs_partial)}</td>
                                  <td></td>
                                  <td></td>
                                </tr>
                                {open && breakdowns.map(([heading, rows]) => (
                                  rows.length === 0 ? null : (
                                    <React.Fragment key={`${pkey}-${heading}`}>
                                      <tr style={{ backgroundColor: '#f3f4f6' }}>
                                        <td></td>
                                        <td colSpan={8} style={{
                                          paddingLeft: '3.5rem', fontSize: '0.72rem', fontWeight: 700,
                                          letterSpacing: '0.04em', textTransform: 'uppercase', color: '#6b7280',
                                        }}>
                                          {heading}
                                        </td>
                                      </tr>
                                      {rows.map((b: any) => (
                                        <tr key={`${pkey}-${heading}-${b.label}`} style={{ backgroundColor: '#f9fafb' }}>
                                          <td></td>
                                          {/* The source is the whole point: hover to see the
                                              field and value this figure was read from. */}
                                          <td style={{ paddingLeft: '4.5rem', color: '#555', fontSize: '0.88rem' }}
                                              title={b.sources && b.sources.length
                                                ? `from ${b.sources.join('; ')} — ${b.lines} line${b.lines === 1 ? '' : 's'}`
                                                : undefined}>
                                            {b.label}
                                            {b.sources && b.sources.some((s: string) => s.startsWith('derived')) && (
                                              <span style={{ color: '#9ca3af', fontSize: '0.75rem', marginLeft: '0.4rem' }}>
                                                derived
                                              </span>
                                            )}
                                          </td>
                                          <td className="number-cell" style={{ color: '#555', fontSize: '0.88rem' }}>
                                            {formatCurrency(b.pre_tax)}
                                          </td>
                                          <td></td>
                                          <td></td>
                                          <td className="number-cell" style={{ color: '#555', fontSize: '0.88rem' }}>
                                            {formatNumber(b.units)}
                                          </td>
                                          <td className="number-cell" style={{ color: '#555', fontSize: '0.88rem' }}>
                                            {formatNumber(b.lbs)}
                                          </td>
                                          <td></td>
                                          <td></td>
                                        </tr>
                                      ))}
                                    </React.Fragment>
                                  )
                                ))}
                              </React.Fragment>
                            );
                          })}
                        </React.Fragment>
                      ))}
                      <tr className="grand-total-row">
                        <td></td>
                        <td><strong>TOTAL</strong></td>
                        <td className="number-cell"><strong>{formatCurrency(categorySales.totals.pre_tax)}</strong></td>
                        <td className="number-cell"><strong>{formatCurrency(categorySales.totals.tax)}</strong></td>
                        <td className="number-cell"><strong>{formatCurrency(categorySales.totals.post_tax)}</strong></td>
                        <td className="number-cell"><strong>{formatNumber(categorySales.totals.units)}</strong></td>
                        <td className="number-cell"><strong>{formatLbs(categorySales.totals.lbs, categorySales.totals.lbs_partial)}</strong></td>
                        <td className="number-cell"><strong>{categorySales.totals.transactions}</strong></td>
                        <td className="number-cell"><strong>100%</strong></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Forecasting Tab */}
      {activeTab === 'forecasting' && forecasting && (
        <div className="tab-content">
          {/* Predictions */}
          <div className="forecast-section">
            <h3>Demand Forecast</h3>
            <div className="forecast-cards">
              <div className="forecast-card">
                <div className="forecast-label">Next Month Predicted Revenue</div>
                <div className="forecast-value">{formatCurrency(forecasting.predictions.next_month_revenue)}</div>
                <div className="forecast-sub">Based on 12-month trend</div>
              </div>
              <div className="forecast-card">
                <div className="forecast-label">Next Month Predicted Volume</div>
                <div className="forecast-value">{formatNumber(forecasting.predictions.next_month_lbs)} lbs</div>
                <div className="forecast-sub">Based on 12-month trend</div>
              </div>
              <div className="forecast-card">
                <div className="forecast-label">Monthly Average Orders</div>
                <div className="forecast-value">{forecasting.predictions.monthly_avg_orders.toFixed(1)}</div>
                <div className="forecast-sub">Last 12 months</div>
              </div>
              <div className="forecast-card">
                <div className="forecast-label">Trend</div>
                <div className={`forecast-value trend ${forecasting.predictions.trend_percentage >= 0 ? 'positive' : 'negative'}`}>
                  {forecasting.predictions.trend_percentage >= 0 ? '+' : ''}{forecasting.predictions.trend_percentage.toFixed(1)}%
                </div>
                <div className="forecast-sub">Recent 3 months vs previous 3</div>
              </div>
            </div>
          </div>

          {/* Inactive Customers */}
          <div className="forecast-section">
            <div className="section-header">
              <h3>Inactive Customers ({forecasting.inactive_customer_count})</h3>
              <span className="section-subtitle">No orders in the last 60 days</span>
            </div>
            <div className="inactive-customers-grid">
              {forecasting.inactive_customers.map((customer) => (
                <div key={customer.customer_id} className="inactive-customer-card">
                  <div className="inactive-customer-name">{customer.customer_name}</div>
                  <div className="inactive-customer-info">
                    <span className="days-inactive">{customer.days_since_order} days</span>
                    <span className="last-order">Last order: {formatDate(customer.last_order_date)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* QR Analytics Tab */}
      {activeTab === 'qr-analytics' && (
        <div className="tab-content">
          {qrLoading ? (
            <div className="admin-reports-loading">
              <div className="spinner"></div>
              <p>Loading QR analytics...</p>
            </div>
          ) : qrAnalytics ? (
            <>
              {/* Time-Based Overview Cards */}
              <div className="summary-cards" style={{ marginBottom: '1rem' }}>
                <div className="summary-card">
                  <div className="card-icon qr-icon">
                    <span>QR</span>
                  </div>
                  <div className="card-content">
                    <div className="card-value">{formatNumber(qrAnalytics.summary.total_scans)}</div>
                    <div className="card-label">Total QR Scans</div>
                  </div>
                </div>
                <div className="summary-card">
                  <div className="card-icon" style={{ background: '#E3F2FD', color: '#1976D2' }}>
                    <span>24h</span>
                  </div>
                  <div className="card-content">
                    <div className="card-value">{formatNumber(qrAnalytics.summary.scans_today || 0)}</div>
                    <div className="card-label">Scans Today</div>
                  </div>
                </div>
                <div className="summary-card">
                  <div className="card-icon" style={{ background: '#E8F5E9', color: '#388E3C' }}>
                    <span>7d</span>
                  </div>
                  <div className="card-content">
                    <div className="card-value">
                      {formatNumber(qrAnalytics.summary.scans_this_week || 0)}
                      {qrAnalytics.summary.week_over_week_trend !== 0 && (
                        <span style={{
                          fontSize: '0.75rem',
                          marginLeft: '0.5rem',
                          color: qrAnalytics.summary.week_over_week_trend > 0 ? '#388E3C' : '#D32F2F'
                        }}>
                          {qrAnalytics.summary.week_over_week_trend > 0 ? '↑' : '↓'}
                          {Math.abs(qrAnalytics.summary.week_over_week_trend)}%
                        </span>
                      )}
                    </div>
                    <div className="card-label">This Week</div>
                  </div>
                </div>
                <div className="summary-card">
                  <div className="card-icon" style={{ background: '#FFF3E0', color: '#F57C00' }}>
                    <span>30d</span>
                  </div>
                  <div className="card-content">
                    <div className="card-value">{formatNumber(qrAnalytics.summary.scans_this_month || 0)}</div>
                    <div className="card-label">This Month</div>
                  </div>
                </div>
              </div>

              {/* Conversion & Partner Cards */}
              <div className="summary-cards">
                <div className="summary-card">
                  <div className="card-icon signup-icon">
                    <span>+</span>
                  </div>
                  <div className="card-content">
                    <div className="card-value">{formatNumber(qrAnalytics.summary.total_signups)}</div>
                    <div className="card-label">Total Signups</div>
                  </div>
                </div>
                <div className="summary-card">
                  <div className="card-icon conversion-icon">
                    <span>%</span>
                  </div>
                  <div className="card-content">
                    <div className="card-value">{qrAnalytics.summary.overall_conversion_rate}%</div>
                    <div className="card-label">Conversion Rate</div>
                  </div>
                </div>
                <div className="summary-card">
                  <div className="card-icon" style={{ background: '#FCE4EC', color: '#C2185B' }}>
                    <span>+{qrAnalytics.summary.signups_this_month || 0}</span>
                  </div>
                  <div className="card-content">
                    <div className="card-value">{formatNumber(qrAnalytics.summary.signups_this_month || 0)}</div>
                    <div className="card-label">Signups This Month</div>
                  </div>
                </div>
                <div className="summary-card">
                  <div className="card-icon partner-icon">
                    <span>★</span>
                  </div>
                  <div className="card-content">
                    <div className="card-value" style={{ fontSize: '1rem' }}>
                      {qrAnalytics.summary.most_active_partner || '—'}
                    </div>
                    <div className="card-label">Most Active Partner</div>
                  </div>
                </div>
              </div>

              {/* Placement Performance Table */}
              <div className="chart-card" style={{ marginBottom: '1.5rem' }}>
                <h3>Placement Performance (Last 30 Days)</h3>
                <div className="data-table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Placement</th>
                        <th className="number-cell">Scans</th>
                        <th className="number-cell">Signups</th>
                        <th className="number-cell">Conversion</th>
                        <th>Best Location</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(qrAnalytics.placement_performance || []).map((p) => (
                        <tr key={p.placement}>
                          <td><strong>{p.label}</strong></td>
                          <td className="number-cell">{formatNumber(p.scans)}</td>
                          <td className="number-cell">{formatNumber(p.signups)}</td>
                          <td className="number-cell">
                            <span className={`conversion-badge ${p.conversion_rate >= 10 ? 'good' : p.conversion_rate >= 5 ? 'ok' : 'low'}`}>
                              {p.conversion_rate}%
                            </span>
                          </td>
                          <td>{p.best_location || '—'}</td>
                        </tr>
                      ))}
                      {(qrAnalytics.placement_performance || []).length === 0 && (
                        <tr>
                          <td colSpan={5} style={{ textAlign: 'center', color: '#666' }}>
                            No QR scan data available yet
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Retail Partner Performance Table */}
              <div className="chart-card" style={{ marginBottom: '1.5rem' }}>
                <h3>Retail Partner Performance (Last 30 Days)</h3>
                <div className="data-table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Retail Partner</th>
                        <th className="number-cell">Total Scans</th>
                        <th className="number-cell">Floor</th>
                        <th className="number-cell">Register</th>
                        <th className="number-cell">Shelf</th>
                        <th className="number-cell">Bag</th>
                        <th className="number-cell">Signups</th>
                        <th className="number-cell">Conversion</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(qrAnalytics.store_performance || []).map((store) => (
                        <tr key={store.store_code || store.store_id} style={{ cursor: 'pointer' }} title="Click to view details">
                          <td><strong>{store.store_name}</strong></td>
                          <td className="number-cell">{formatNumber(store.total_scans)}</td>
                          <td className="number-cell">{store.floor_scans || '—'}</td>
                          <td className="number-cell">{store.register_scans || '—'}</td>
                          <td className="number-cell">{store.shelf_scans || '—'}</td>
                          <td className="number-cell">{store.bag_scans || '—'}</td>
                          <td className="number-cell">{formatNumber(store.signups)}</td>
                          <td className="number-cell">
                            <span className={`conversion-badge ${store.conversion_rate >= 10 ? 'good' : store.conversion_rate >= 5 ? 'ok' : 'low'}`}>
                              {store.conversion_rate}%
                            </span>
                          </td>
                        </tr>
                      ))}
                      {(qrAnalytics.store_performance || []).length === 0 && (
                        <tr>
                          <td colSpan={8} style={{ textAlign: 'center', color: '#666' }}>
                            No retail partner scan data available yet
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Recent Scans Table */}
              <div className="chart-card">
                <h3>Recent QR Scans</h3>
                <div className="data-table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Time</th>
                        <th>Store</th>
                        <th>Placement</th>
                        <th>Device</th>
                        <th>Converted</th>
                        <th>Member</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(qrAnalytics.recent_scans || []).map((scan) => (
                        <tr key={scan.id}>
                          <td>{new Date(scan.visited_at).toLocaleString()}</td>
                          <td><strong>{scan.store_name}</strong></td>
                          <td>{scan.placement_label}</td>
                          <td>
                            <span style={{
                              padding: '0.125rem 0.5rem',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              background: scan.device_type === 'mobile' ? '#E3F2FD' : '#F3E5F5',
                              color: scan.device_type === 'mobile' ? '#1565C0' : '#7B1FA2'
                            }}>
                              {scan.device_type}
                            </span>
                          </td>
                          <td>
                            {scan.converted ? (
                              <span style={{ color: '#388E3C', fontWeight: 600 }}>✓ Yes</span>
                            ) : (
                              <span style={{ color: '#9E9E9E' }}>No</span>
                            )}
                          </td>
                          <td>{scan.member_name || '—'}</td>
                        </tr>
                      ))}
                      {(qrAnalytics.recent_scans || []).length === 0 && (
                        <tr>
                          <td colSpan={6} style={{ textAlign: 'center', color: '#666' }}>
                            No recent scans
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : (
            <div className="empty-state">
              <p>Failed to load QR analytics. Please try again.</p>
              <button onClick={loadQRAnalytics} className="btn-retry">Retry</button>
            </div>
          )}
        </div>
      )}

      {/* Tax Report Tab */}
      {activeTab === 'tax' && (
        <div className="tab-content">
          {/* Cash basis badge */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <span
              title={
                taxSummary?.basis_note ||
                'Wholesale orders counted when payment received, not when ordered. POS and Bridge Club use transaction date.'
              }
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '4px',
                padding: '3px 10px', borderRadius: '12px',
                backgroundColor: '#dcfce7', color: '#166534',
                fontSize: '12px', fontWeight: 600, border: '1px solid #86efac',
                cursor: 'help',
              }}
            >
              Cash Basis
            </span>
            <span style={{ fontSize: '12px', color: '#6b7280' }}>
              Wholesale revenue counted on payment date · POS &amp; Bridge Club on transaction date
            </span>
          </div>

          {/* Date Presets */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
            {[
              { key: 'this_month', label: 'This Month' },
              { key: 'last_month', label: 'Last Month' },
              { key: 'this_quarter', label: 'This Quarter' },
              { key: 'last_quarter', label: 'Last Quarter' },
              { key: 'jan_jun', label: 'Jan-Jun' },
              { key: 'jul_dec', label: 'Jul-Dec' },
              { key: 'this_year', label: 'This Year' },
            ].map(p => (
              <button
                key={p.key}
                onClick={() => applyTaxPreset(p.key)}
                className={`btn-export-small ${taxPreset === p.key ? 'active' : ''}`}
                style={{
                  backgroundColor: taxPreset === p.key ? '#1a4d1a' : '#e0e0e0',
                  color: taxPreset === p.key ? '#fff' : '#333',
                  border: 'none', padding: '0.4rem 0.75rem', borderRadius: '4px', cursor: 'pointer',
                }}
              >
                {p.label}
              </button>
            ))}
            <button
              className="btn-export-small"
              onClick={() => {
                const url = adminReportsAPI.exportReportCSV('tax_summary', { date_from: dateFrom, date_to: dateTo });
                window.open(url, '_blank');
              }}
              style={{ marginLeft: 'auto' }}
            >
              Export CSV
            </button>
          </div>

          {taxSummary ? (
            <>
              {/* Summary Cards */}
              <div className="summary-cards">
                <div className="summary-card">
                  <div className="card-icon revenue-icon"><span>$</span></div>
                  <div className="card-content">
                    <div className="card-value">{formatCurrency(taxSummary.totals.gross_sales)}</div>
                    <div className="card-label">Gross Sales</div>
                  </div>
                </div>
                <div className="summary-card">
                  <div className="card-icon orders-icon"><span>Tax</span></div>
                  <div className="card-content">
                    <div className="card-value">{formatCurrency(taxSummary.totals.taxable_sales)}</div>
                    <div className="card-label">Taxable Sales</div>
                  </div>
                </div>
                <div className="summary-card">
                  <div className="card-icon lbs-icon"><span>Ex</span></div>
                  <div className="card-content">
                    <div className="card-value">{formatCurrency(taxSummary.totals.exempt_sales)}</div>
                    <div className="card-label">Tax-Exempt Sales</div>
                  </div>
                </div>
                <div className="summary-card">
                  <div className="card-icon" style={{ background: '#FFF3E0', color: '#E65100' }}><span>$</span></div>
                  <div className="card-content">
                    <div className="card-value">{formatCurrency(taxSummary.totals.tax_collected)}</div>
                    <div className="card-label">Tax Collected</div>
                  </div>
                </div>
                <div className="summary-card">
                  <div className="card-icon" style={{ background: '#E3F2FD', color: '#1565C0' }}><span>%</span></div>
                  <div className="card-content">
                    <div className="card-value">{taxSummary.totals.effective_rate}%</div>
                    <div className="card-label">Effective Tax Rate</div>
                  </div>
                </div>
              </div>

              {/* Daily Breakdown Chart */}
              {taxSummary.daily_breakdown.length > 0 && (
                <div className="chart-card wide" style={{ marginBottom: '1.5rem' }}>
                  <h3>Daily Tax Breakdown</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={taxSummary.daily_breakdown}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}k`} />
                      <Tooltip formatter={(value: number) => [`$${value.toFixed(2)}`, '']} />
                      <Legend />
                      <Bar dataKey="taxable_sales" name="Taxable" fill="#508590" stackId="a" />
                      <Bar dataKey="exempt_sales" name="Exempt" fill="#C3AD8B" stackId="a" />
                      <Bar dataKey="tax_collected" name="Tax Collected" fill="#E73C3E" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Quarterly Rollup */}
              {taxSummary.quarterly_rollup.length > 0 && (
                <div className="chart-card" style={{ marginBottom: '1.5rem' }}>
                  <h3>Quarterly Rollup</h3>
                  <div className="data-table-wrapper">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Quarter</th>
                          <th className="number-cell">Taxable Sales</th>
                          <th className="number-cell">Exempt Sales</th>
                          <th className="number-cell">Tax Collected</th>
                        </tr>
                      </thead>
                      <tbody>
                        {taxSummary.quarterly_rollup.map((q: any) => (
                          <tr key={q.quarter}>
                            <td><strong>{q.quarter}</strong></td>
                            <td className="number-cell">{formatCurrency(q.taxable)}</td>
                            <td className="number-cell">{formatCurrency(q.exempt)}</td>
                            <td className="number-cell">{formatCurrency(q.tax)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Tax Exempt Log */}
              {taxExemptLog && taxExemptLog.exempt_transactions.length > 0 && (
                <div className="chart-card">
                  <div className="chart-header">
                    <h3>Tax-Exempt Transactions ({taxExemptLog.total_count})</h3>
                    <button
                      className="btn-export-small"
                      onClick={() => {
                        const url = adminReportsAPI.exportReportCSV('tax_exempt_log', { date_from: dateFrom, date_to: dateTo });
                        window.open(url, '_blank');
                      }}
                    >
                      Export CSV
                    </button>
                  </div>
                  <div className="data-table-wrapper">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Time</th>
                          <th>Txn #</th>
                          <th>Channel</th>
                          <th>Customer</th>
                          <th className="number-cell">Subtotal</th>
                          <th className="number-cell">Total</th>
                          <th className="number-cell">Items</th>
                        </tr>
                      </thead>
                      <tbody>
                        {taxExemptLog.exempt_transactions.map((t: any, i: number) => (
                          <tr key={i}>
                            <td>{t.date}</td>
                            <td>{t.time}</td>
                            <td style={{ fontFamily: 'monospace' }}>{t.transaction_number}</td>
                            <td><span className="type-badge">{t.channel}</span></td>
                            <td>{t.customer_name || '\u2014'}</td>
                            <td className="number-cell">${t.subtotal.toFixed(2)}</td>
                            <td className="number-cell">${t.total.toFixed(2)}</td>
                            <td className="number-cell">{t.item_count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="admin-reports-loading"><div className="spinner"></div><p>Loading tax report...</p></div>
          )}
        </div>
      )}

      {/* Payments & AR Tab */}
      {activeTab === 'payments' && (
        <div className="tab-content">
          {paymentData ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
                <button
                  className="btn-export-small"
                  onClick={() => {
                    const url = adminReportsAPI.exportReportCSV('payment_breakdown', { date_from: dateFrom, date_to: dateTo });
                    window.open(url, '_blank');
                  }}
                >
                  Export CSV
                </button>
              </div>

              {/* Payment Method Cards */}
              <div className="summary-cards">
                {Object.entries(paymentData.payment_summary as Record<string, { amount: number; count: number }>).map(([method, data]) => (
                  <div className="summary-card" key={method}>
                    <div className="card-content">
                      <div className="card-value" style={data.amount < 0 ? { color: '#d32f2f' } : undefined}>{formatCurrency(data.amount)}</div>
                      <div className="card-label">{paymentMethodLabel(method)}</div>
                      {data.count > 0 && <div style={{ fontSize: '0.8rem', color: '#666' }}>{data.count} txns</div>}
                    </div>
                  </div>
                ))}
                <div className="summary-card">
                  <div className="card-content">
                    <div className="card-value" style={{ color: '#e65100' }}>{formatCurrency(paymentData.surcharges)}</div>
                    <div className="card-label">Surcharges (Fee Saver)</div>
                  </div>
                </div>
              </div>

              {/* Payment Pie Chart */}
              <div className="charts-row">
                <div className="chart-card">
                  <h3>Revenue by Payment Method</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={Object.entries(paymentData.payment_summary as Record<string, { amount: number }>)
                          .filter(([, d]) => d.amount > 0)
                          .map(([name, d]) => ({ name: paymentMethodLabel(name), value: d.amount }))}
                        cx="50%" cy="50%" outerRadius={100} dataKey="value"
                        label={(props: any) => `${props.name || ''} ${((props.percent || 0) * 100).toFixed(0)}%`}
                      >
                        {Object.entries(paymentData.payment_summary as Record<string, { amount: number }>)
                          .filter(([, d]) => d.amount > 0)
                          .map(([, ], i) => <Cell key={i} fill={PAYMENT_COLORS[i % PAYMENT_COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(value: number) => formatCurrency(value)} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* Outstanding Invoices */}
                <div className="chart-card">
                  <h3>Outstanding Invoices Aging</h3>
                  <div style={{ padding: '1rem' }}>
                    {[
                      { label: 'Current (0-30 days)', value: paymentData.outstanding_invoices.current, color: '#2e7d32' },
                      { label: '31-60 days', value: paymentData.outstanding_invoices.over_30, color: '#e65100' },
                      { label: '61-90 days', value: paymentData.outstanding_invoices.over_60, color: '#d32f2f' },
                      { label: '90+ days', value: paymentData.outstanding_invoices.over_90, color: '#b71c1c' },
                    ].map(bucket => (
                      <div key={bucket.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem 0', borderBottom: '1px solid #eee' }}>
                        <span>{bucket.label}</span>
                        <span style={{ fontWeight: 'bold', color: bucket.color }}>{formatCurrency(bucket.value)}</span>
                      </div>
                    ))}
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem 0', fontWeight: 'bold', fontSize: '1.1rem' }}>
                      <span>Total Outstanding</span>
                      <span>{formatCurrency(paymentData.outstanding_invoices.total)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="admin-reports-loading"><div className="spinner"></div><p>Loading payment data...</p></div>
          )}
        </div>
      )}

      {/* POS Report Tab */}
      {activeTab === 'pos' && (
        <div className="tab-content">
          {posSummary ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
                <button
                  className="btn-export-small"
                  onClick={() => {
                    const url = adminReportsAPI.exportReportCSV('pos_summary', { date_from: dateFrom, date_to: dateTo });
                    window.open(url, '_blank');
                  }}
                >
                  Export CSV
                </button>
              </div>

              {/* POS Daily Trend Chart */}
              {posSummary.daily_summary.length > 0 && (
                <div className="chart-card wide" style={{ marginBottom: '1.5rem' }}>
                  <h3>POS Daily Sales</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={posSummary.daily_summary}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}k`} />
                      <Tooltip formatter={(value: number) => [`$${value.toFixed(2)}`, '']} />
                      <Legend />
                      <Bar dataKey="revenue" name="Revenue" fill="#508590" />
                      <Bar dataKey="tax" name="Tax" fill="#E73C3E" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Cashier Performance */}
              {posSummary.cashier_performance.length > 0 && (
                <div className="chart-card" style={{ marginBottom: '1.5rem' }}>
                  <h3>Cashier Performance</h3>
                  <div className="data-table-wrapper">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Cashier</th>
                          <th className="number-cell">Transactions</th>
                          <th className="number-cell">Total Revenue</th>
                          <th className="number-cell">Avg Transaction</th>
                        </tr>
                      </thead>
                      <tbody>
                        {posSummary.cashier_performance.map((c: any) => (
                          <tr key={c.cashier_id}>
                            <td><strong>{c.cashier_name}</strong></td>
                            <td className="number-cell">{c.transaction_count}</td>
                            <td className="number-cell">{formatCurrency(c.total_revenue)}</td>
                            <td className="number-cell">{formatCurrency(c.avg_transaction)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Shift History */}
              {posSummary.shifts.length > 0 && (
                <div className="chart-card">
                  <h3>Shift History</h3>
                  <div className="data-table-wrapper">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Cashier</th>
                          <th>Opened</th>
                          <th>Closed</th>
                          <th>Status</th>
                          <th className="number-cell">Opening Cash</th>
                          <th className="number-cell">Closing Cash</th>
                          <th className="number-cell">Variance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {posSummary.shifts.map((s: any) => (
                          <tr key={s.id}>
                            <td>{s.cashier}</td>
                            <td>{new Date(s.opened_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                            <td>{s.closed_at ? new Date(s.closed_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '\u2014'}</td>
                            <td>
                              <span className={`status-badge ${s.status}`}>{s.status}</span>
                            </td>
                            <td className="number-cell">${s.opening_cash.toFixed(2)}</td>
                            <td className="number-cell">{s.closing_cash != null ? `$${s.closing_cash.toFixed(2)}` : '\u2014'}</td>
                            <td className="number-cell" style={{
                              color: s.cash_variance != null ? (Math.abs(s.cash_variance) < 1 ? '#2e7d32' : '#d32f2f') : '#666',
                              fontWeight: 'bold',
                            }}>
                              {s.cash_variance != null ? `$${s.cash_variance.toFixed(2)}` : '\u2014'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="admin-reports-loading"><div className="spinner"></div><p>Loading POS data...</p></div>
          )}
        </div>
      )}

      {/* Feed License Tab */}
      {activeTab === 'feed-license' && (
        <div className="tab-content">
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div className="filter-group">
              <label>Period</label>
              <select value={licenseHalf} onChange={e => setLicenseHalf(e.target.value)}>
                <option value="1">Jan - Jun</option>
                <option value="2">Jul - Dec</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Year</label>
              <select value={licenseYear} onChange={e => setLicenseYear(parseInt(e.target.value))}>
                {[...Array(5)].map((_, i) => {
                  const yr = new Date().getFullYear() - i;
                  return <option key={yr} value={yr}>{yr}</option>;
                })}
              </select>
            </div>
            <button
              className="btn-export-small"
              onClick={() => {
                const url = adminReportsAPI.exportReportCSV('feed_license_report', { half: licenseHalf, year: String(licenseYear) });
                window.open(url, '_blank');
              }}
            >
              Export CSV
            </button>
          </div>

          {feedLicense ? (
            <>
              {/* Summary Cards */}
              <div className="summary-cards">
                <div className="summary-card">
                  <div className="card-icon lbs-icon"><span>Tons</span></div>
                  <div className="card-content">
                    <div className="card-value">{feedLicense.total_tons_produced.toFixed(2)}</div>
                    <div className="card-label">Total Tons Produced</div>
                    <div style={{ fontSize: '0.8rem', color: '#666' }}>{feedLicense.period}</div>
                  </div>
                </div>
                <div className="summary-card">
                  <div className="card-icon revenue-icon"><span>Sold</span></div>
                  <div className="card-content">
                    <div className="card-value">{feedLicense.total_tons_sold.toFixed(2)}</div>
                    <div className="card-label">Total Tons Sold</div>
                    <div style={{ fontSize: '0.8rem', color: '#666' }}>All channels</div>
                  </div>
                </div>
              </div>

              {/* Section 1: Tonnage by Feed Line */}
              <div className="collapsible-section">
                <div
                  className="section-toggle"
                  onClick={() => setFeedLicenseExpanded(prev => ({ ...prev, feedLine: !prev.feedLine }))}
                >
                  <span className={`toggle-arrow ${feedLicenseExpanded.feedLine ? 'open' : ''}`}>&#9654;</span>
                  <h3>Tonnage by Feed Line &amp; Type</h3>
                </div>
                {feedLicenseExpanded.feedLine && feedLicense.products_by_feed_line && (
                  <div className="section-body">
                    {['Five Star', 'Plus', 'Essentials', 'Other'].map(lineName => {
                      const section = feedLicense.products_by_feed_line[lineName];
                      if (!section || section.products.length === 0) return null;
                      return (
                        <div key={lineName} className="feed-line-group">
                          <div className="feed-line-header">
                            <strong>{lineName}</strong>
                            <span className="feed-line-subtotal">
                              {section.subtotal_produced.toFixed(2)} produced / {section.subtotal_sold.toFixed(2)} sold
                            </span>
                          </div>
                          <div className="data-table-wrapper">
                            <table className="data-table">
                              <thead>
                                <tr>
                                  <th>Product</th>
                                  <th>Code</th>
                                  <th>Animal Type</th>
                                  <th className="number-cell">Tons Produced</th>
                                  <th className="number-cell">Tons Sold</th>
                                </tr>
                              </thead>
                              <tbody>
                                {section.products.map((p: any, i: number) => (
                                  <tr key={i}>
                                    <td><strong>{p.product}</strong></td>
                                    <td>{p.code}</td>
                                    <td>{p.animal_group}</td>
                                    <td className="number-cell">{p.tons_produced.toFixed(2)}</td>
                                    <td className="number-cell">{p.tons_sold.toFixed(2)}</td>
                                  </tr>
                                ))}
                                <tr className="subtotal-row">
                                  <td colSpan={3}><strong>{lineName} Subtotal</strong></td>
                                  <td className="number-cell"><strong>{section.subtotal_produced.toFixed(2)}</strong></td>
                                  <td className="number-cell"><strong>{section.subtotal_sold.toFixed(2)}</strong></td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    })}
                    <div className="data-table-wrapper" style={{ marginTop: '0.5rem' }}>
                      <table className="data-table">
                        <tbody>
                          <tr className="grand-total-row">
                            <td colSpan={3}><strong>GRAND TOTAL</strong></td>
                            <td className="number-cell"><strong>{feedLicense.total_tons_produced.toFixed(2)}</strong></td>
                            <td className="number-cell"><strong>{feedLicense.total_tons_sold.toFixed(2)}</strong></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              {/* Section 2: Tonnage by Animal Group + Sales Channel */}
              <div className="collapsible-section">
                <div
                  className="section-toggle"
                  onClick={() => setFeedLicenseExpanded(prev => ({ ...prev, animalGroup: !prev.animalGroup }))}
                >
                  <span className={`toggle-arrow ${feedLicenseExpanded.animalGroup ? 'open' : ''}`}>&#9654;</span>
                  <h3>Tonnage by Animal Group</h3>
                </div>
                {feedLicenseExpanded.animalGroup && feedLicense.animal_group_summary && feedLicense.animal_group_summary.length > 0 && (
                  <div className="section-body">
                    <div className="data-table-wrapper">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Animal Group</th>
                            <th className="number-cell">Tons Produced</th>
                            <th className="number-cell">Tons Sold</th>
                            <th className="number-cell">Wholesale</th>
                            <th className="number-cell">Retail Partners</th>
                            <th className="number-cell">Local Store (POS)</th>
                            <th className="number-cell">Bridge Club</th>
                          </tr>
                        </thead>
                        <tbody>
                          {feedLicense.animal_group_summary.map((a: any) => {
                            const rations = a.rations || [];
                            const open = !!expandedAnimalGroups[a.animal_group];
                            return (
                              <React.Fragment key={a.animal_group}>
                                <tr
                                  className="expandable-row"
                                  style={{ cursor: rations.length ? 'pointer' : 'default' }}
                                  onClick={() => rations.length && setExpandedAnimalGroups(prev => ({
                                    ...prev, [a.animal_group]: !prev[a.animal_group],
                                  }))}
                                >
                                  <td>
                                    {rations.length > 0 && (
                                      <span className={`toggle-arrow ${open ? 'open' : ''}`}>&#9654;</span>
                                    )}
                                    <strong>{a.animal_group}</strong>
                                  </td>
                                  <td className="number-cell">{a.tons_produced.toFixed(2)}</td>
                                  <td className="number-cell">{a.tons_sold.toFixed(2)}</td>
                                  <td className="number-cell">{a.wholesale.toFixed(2)}</td>
                                  <td className="number-cell">{a.retail_partners.toFixed(2)}</td>
                                  <td className="number-cell">{a.local_store.toFixed(2)}</td>
                                  <td className="number-cell">{a.bridge_club.toFixed(2)}</td>
                                </tr>
                                {open && rations.map((r: any) => (
                                  <tr key={`${a.animal_group}-${r.code}`} className="ration-row">
                                    <td style={{ paddingLeft: '2rem' }}>
                                      <span className="ration-code">{r.code}</span>{' '}
                                      <span className="ration-name">{r.name}</span>
                                    </td>
                                    <td className="number-cell">{r.tons_produced.toFixed(2)}</td>
                                    <td className="number-cell">{r.tons_sold.toFixed(2)}</td>
                                    <td className="number-cell">{r.wholesale.toFixed(2)}</td>
                                    <td className="number-cell">{r.retail_partners.toFixed(2)}</td>
                                    <td className="number-cell">{r.local_store.toFixed(2)}</td>
                                    <td className="number-cell">{r.bridge_club.toFixed(2)}</td>
                                  </tr>
                                ))}
                              </React.Fragment>
                            );
                          })}
                          <tr className="grand-total-row">
                            <td><strong>TOTAL</strong></td>
                            <td className="number-cell">
                              <strong>{feedLicense.animal_group_summary.reduce((s: number, a: any) => s + a.tons_produced, 0).toFixed(2)}</strong>
                            </td>
                            <td className="number-cell">
                              <strong>{feedLicense.animal_group_summary.reduce((s: number, a: any) => s + a.tons_sold, 0).toFixed(2)}</strong>
                            </td>
                            <td className="number-cell">
                              <strong>{feedLicense.animal_group_summary.reduce((s: number, a: any) => s + a.wholesale, 0).toFixed(2)}</strong>
                            </td>
                            <td className="number-cell">
                              <strong>{feedLicense.animal_group_summary.reduce((s: number, a: any) => s + a.retail_partners, 0).toFixed(2)}</strong>
                            </td>
                            <td className="number-cell">
                              <strong>{feedLicense.animal_group_summary.reduce((s: number, a: any) => s + a.local_store, 0).toFixed(2)}</strong>
                            </td>
                            <td className="number-cell">
                              <strong>{feedLicense.animal_group_summary.reduce((s: number, a: any) => s + a.bridge_club, 0).toFixed(2)}</strong>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              {/* Section 3: Shipments by State */}
              <div className="collapsible-section">
                <div
                  className="section-toggle"
                  onClick={() => setFeedLicenseExpanded(prev => ({ ...prev, stateShipments: !prev.stateShipments }))}
                >
                  <span className={`toggle-arrow ${feedLicenseExpanded.stateShipments ? 'open' : ''}`}>&#9654;</span>
                  <h3>Shipments by State</h3>
                </div>
                {feedLicenseExpanded.stateShipments && feedLicense.state_shipments && feedLicense.state_shipments.length > 0 && (
                  <div className="section-body">
                    <div style={{ marginBottom: '0.75rem' }}>
                      <select
                        value={selectedState}
                        onChange={e => setSelectedState(e.target.value)}
                        style={{ padding: '0.4rem 0.75rem', borderRadius: '6px', border: '1px solid #ddd' }}
                      >
                        <option value="">All States</option>
                        {feedLicense.state_shipments.map((s: any) => (
                          <option key={s.state} value={s.state}>{s.state} ({s.total_tons.toFixed(2)} tons)</option>
                        ))}
                      </select>
                    </div>

                    {!selectedState ? (
                      /* Summary: all states */
                      <div className="data-table-wrapper">
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th>State</th>
                              <th className="number-cell">Total Tons</th>
                              <th>Top Products</th>
                            </tr>
                          </thead>
                          <tbody>
                            {feedLicense.state_shipments.map((s: any) => (
                              <tr key={s.state} style={{ cursor: 'pointer' }} onClick={() => setSelectedState(s.state)}>
                                <td><strong>{s.state}</strong></td>
                                <td className="number-cell">{s.total_tons.toFixed(2)}</td>
                                <td style={{ fontSize: '0.85rem', color: '#666' }}>
                                  {s.products.slice(0, 3).map((p: any) => p.product).join(', ')}
                                  {s.products.length > 3 ? ` +${s.products.length - 3} more` : ''}
                                </td>
                              </tr>
                            ))}
                            <tr className="grand-total-row">
                              <td><strong>TOTAL</strong></td>
                              <td className="number-cell">
                                <strong>{feedLicense.state_shipments.reduce((s: number, st: any) => s + st.total_tons, 0).toFixed(2)}</strong>
                              </td>
                              <td></td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      /* Detail: specific state */
                      (() => {
                        const stateData = feedLicense.state_shipments.find((s: any) => s.state === selectedState);
                        if (!stateData) return <p>No data for {selectedState}</p>;
                        return (
                          <div className="data-table-wrapper">
                            <h4 style={{ margin: '0 0 0.5rem 0' }}>{selectedState} — {stateData.total_tons.toFixed(2)} tons total</h4>
                            <table className="data-table">
                              <thead>
                                <tr>
                                  <th>Product</th>
                                  <th>Animal Group</th>
                                  <th className="number-cell">Tons Shipped</th>
                                </tr>
                              </thead>
                              <tbody>
                                {stateData.products.map((p: any, i: number) => (
                                  <tr key={i}>
                                    <td><strong>{p.product}</strong></td>
                                    <td>{p.animal_group}</td>
                                    <td className="number-cell">{p.tons.toFixed(2)}</td>
                                  </tr>
                                ))}
                                <tr className="subtotal-row">
                                  <td colSpan={2}><strong>State Total</strong></td>
                                  <td className="number-cell"><strong>{stateData.total_tons.toFixed(2)}</strong></td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        );
                      })()
                    )}
                  </div>
                )}
              </div>
            </>
          ) : feedLicenseError ? (
            <div className="admin-reports-loading"><p style={{ color: '#c00' }}>{feedLicenseError}</p></div>
          ) : (
            <div className="admin-reports-loading"><div className="spinner"></div><p>Loading feed license data...</p></div>
          )}
        </div>
      )}

      {/* Sales Detail Tab */}
      {activeTab === 'sales-detail' && (
        <div className="tab-content">
          {/* Filters */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1rem', alignItems: 'flex-end' }}>
            <div className="filter-group">
              <label>Channel</label>
              <select value={channelFilter} onChange={e => setChannelFilter(e.target.value)}>
                <option value="">All Channels</option>
                <option value="pos">POS</option>
                <option value="wholesale">Wholesale</option>
                <option value="retail_partners">Retail Partners</option>
                <option value="bridge_club">Bridge Club</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Tax Exempt</label>
              <select value={sdTaxExempt} onChange={e => setSdTaxExempt(e.target.value)}>
                <option value="">All</option>
                <option value="yes">Exempt Only</option>
                <option value="no">Taxable Only</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Search</label>
              <input
                type="text"
                placeholder="Order/Txn #..."
                value={sdSearch}
                onChange={e => setSdSearch(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && loadSalesDetail()}
              />
            </div>
            <button onClick={loadSalesDetail} className="btn-search">Search</button>
            <button
              className="btn-export-small"
              onClick={() => {
                const params: Record<string, string> = { date_from: dateFrom, date_to: dateTo };
                if (channelFilter) params.channel = channelFilter;
                if (sdTaxExempt) params.tax_exempt = sdTaxExempt;
                if (sdSearch) params.search = sdSearch;
                const url = adminReportsAPI.exportReportCSV('sales_detail', params);
                window.open(url, '_blank');
              }}
              style={{ marginLeft: 'auto' }}
            >
              Export CSV
            </button>
          </div>

          {salesDetail ? (
            <div className="chart-card">
              <h3>All Transactions ({salesDetail.total_count})</h3>
              <div className="data-table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Order/Txn #</th>
                      <th>Channel</th>
                      <th>Customer</th>
                      <th className="number-cell">Items</th>
                      <th className="number-cell">Subtotal</th>
                      <th className="number-cell">Tax</th>
                      <th className="number-cell">Total</th>
                      <th>Payment</th>
                      <th>Tax Exempt</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {salesDetail.transactions.map((txn: any, i: number) => (
                      <React.Fragment key={`${txn.order_number}-${i}`}>
                        <tr
                          onClick={() => setExpandedSale(expandedSale === txn.order_number ? null : txn.order_number)}
                          style={{ cursor: 'pointer', backgroundColor: expandedSale === txn.order_number ? '#f5f5f5' : undefined }}
                        >
                          <td>{txn.date}{txn.time ? ` ${txn.time}` : ''}</td>
                          <td style={{ fontFamily: 'monospace' }}>{txn.order_number}</td>
                          <td><span className="type-badge">{txn.channel}</span></td>
                          <td>{txn.customer || '\u2014'}</td>
                          <td className="number-cell">{txn.item_count}</td>
                          <td className="number-cell">${txn.subtotal.toFixed(2)}</td>
                          <td className="number-cell">${txn.tax.toFixed(2)}</td>
                          <td className="number-cell" style={{ fontWeight: 'bold' }}>${txn.total.toFixed(2)}</td>
                          <td>{txn.payment_method}</td>
                          <td>{txn.tax_exempt ? <span style={{ color: '#2e7d32', fontWeight: 600 }}>Exempt</span> : '\u2014'}</td>
                          <td><span className={`status-badge ${txn.status.toLowerCase().replace(/\s+/g, '_')}`}>{txn.status}</span></td>
                        </tr>
                        {expandedSale === txn.order_number && txn.items && txn.items.length > 0 && (
                          <tr>
                            <td colSpan={11} style={{ padding: '0.5rem 0.5rem 0.5rem 2rem', backgroundColor: '#fafafa' }}>
                              <table style={{ width: '100%', fontSize: '0.9rem' }}>
                                <thead>
                                  <tr style={{ color: '#666' }}>
                                    <th style={{ textAlign: 'left', padding: '0.25rem' }}>Item</th>
                                    <th style={{ textAlign: 'right', padding: '0.25rem' }}>Qty</th>
                                    <th style={{ textAlign: 'right', padding: '0.25rem' }}>Price</th>
                                    <th style={{ textAlign: 'right', padding: '0.25rem' }}>Total</th>
                                    <th style={{ textAlign: 'right', padding: '0.25rem' }}>Tax</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {txn.items.map((item: any, idx: number) => (
                                    <tr key={idx}>
                                      <td style={{ padding: '0.25rem' }}>{item.name}</td>
                                      <td style={{ padding: '0.25rem', textAlign: 'right' }}>{item.quantity}</td>
                                      <td style={{ padding: '0.25rem', textAlign: 'right' }}>${item.unit_price.toFixed(2)}</td>
                                      <td style={{ padding: '0.25rem', textAlign: 'right' }}>${item.total.toFixed(2)}</td>
                                      <td style={{ padding: '0.25rem', textAlign: 'right' }}>
                                        {item.is_tax_exempt ? <span style={{ color: '#2e7d32' }}>Exempt</span> : `$${item.tax_amount.toFixed(2)}`}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                    {salesDetail.transactions.length === 0 && (
                      <tr>
                        <td colSpan={11} style={{ textAlign: 'center', color: '#999', padding: '2rem' }}>
                          No transactions found for this date range and filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="admin-reports-loading"><div className="spinner"></div><p>Loading sales detail...</p></div>
          )}
        </div>
      )}

      {/* Promo Campaigns Tab */}
      {activeTab === 'promo-campaigns' && (
        <div className="tab-content">
          {newTabLoading ? (
            <div className="admin-reports-loading"><div className="spinner"></div><p>Loading promo campaign data...</p></div>
          ) : promoCampaignReport ? (
            <>
              <div className="summary-cards">
                <div className="summary-card">
                  <div className="card-icon orders-icon"><span>Active</span></div>
                  <div className="card-content">
                    <div className="card-value">{promoCampaignReport.summary?.active_campaigns ?? 0}</div>
                    <div className="card-label">Active Campaigns</div>
                  </div>
                </div>
                <div className="summary-card">
                  <div className="card-icon revenue-icon"><span>$</span></div>
                  <div className="card-content">
                    <div className="card-value">{formatCurrency(promoCampaignReport.summary?.total_invested ?? 0)}</div>
                    <div className="card-label">Total Invested</div>
                  </div>
                </div>
                <div className="summary-card">
                  <div className="card-icon lbs-icon"><span>Rev</span></div>
                  <div className="card-content">
                    <div className="card-value">{formatCurrency(promoCampaignReport.summary?.total_revenue ?? 0)}</div>
                    <div className="card-label">Revenue Generated</div>
                  </div>
                </div>
                <div className="summary-card">
                  <div className="card-icon avg-icon"><span>ROI</span></div>
                  <div className="card-content">
                    <div className="card-value">{(promoCampaignReport.summary?.overall_roi ?? 0).toFixed(1)}%</div>
                    <div className="card-label">Overall ROI</div>
                  </div>
                </div>
              </div>

              <div className="chart-card">
                <h3>Campaign Performance</h3>
                <div className="data-table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Campaign</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th className="number-cell">Enrolled</th>
                        <th className="number-cell">Invested</th>
                        <th className="number-cell">Revenue</th>
                        <th className="number-cell">ROI %</th>
                        <th>Dates</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(promoCampaignReport.campaigns ?? []).map((c: any) => (
                        <tr key={c.id}>
                          <td><strong>{c.name}</strong></td>
                          <td><span className="type-badge">{c.campaign_type}</span></td>
                          <td><span className={`status-badge ${c.status.toLowerCase()}`}>{c.status}</span></td>
                          <td className="number-cell">{c.enrolled_count}</td>
                          <td className="number-cell">{formatCurrency(c.total_invested)}</td>
                          <td className="number-cell">{formatCurrency(c.total_revenue)}</td>
                          <td className="number-cell" style={{ color: c.roi_pct > 0 ? '#2e7d32' : c.roi_pct < 0 ? '#c00' : undefined }}>
                            {c.roi_pct.toFixed(1)}%
                          </td>
                          <td>{c.start_date}{c.end_date ? ` – ${c.end_date}` : ' – ongoing'}</td>
                        </tr>
                      ))}
                      {(promoCampaignReport.campaigns ?? []).length === 0 && (
                        <tr><td colSpan={8} style={{ textAlign: 'center', color: '#999', padding: '2rem' }}>No campaigns found.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : (
            <div className="admin-reports-loading"><p>No data available.</p></div>
          )}
        </div>
      )}

      {/* Shipping Tab */}
      {activeTab === 'shipping' && (
        <div className="tab-content">
          {newTabLoading ? (
            <div className="admin-reports-loading"><div className="spinner"></div><p>Loading shipping data...</p></div>
          ) : shippingReport ? (
            <>
              <div className="summary-cards">
                <div className="summary-card">
                  <div className="card-icon revenue-icon"><span>$</span></div>
                  <div className="card-content">
                    <div className="card-value">{formatCurrency(shippingReport.summary?.total_shipping_charged ?? 0)}</div>
                    <div className="card-label">Total Shipping Charged</div>
                  </div>
                </div>
                <div className="summary-card">
                  <div className="card-icon orders-icon"><span>Disc</span></div>
                  <div className="card-content">
                    <div className="card-value">{formatCurrency(shippingReport.summary?.total_shipping_discounts ?? 0)}</div>
                    <div className="card-label">Shipping Discounts</div>
                  </div>
                </div>
                <div className="summary-card">
                  <div className="card-icon lbs-icon"><span>Net</span></div>
                  <div className="card-content">
                    <div className="card-value">{formatCurrency(shippingReport.summary?.net_shipping_revenue ?? 0)}</div>
                    <div className="card-label">Net Shipping Revenue</div>
                  </div>
                </div>
                <div className="summary-card">
                  <div className="card-icon avg-icon"><span>Free</span></div>
                  <div className="card-content">
                    <div className="card-value">{shippingReport.summary?.free_shipping_orders ?? 0}</div>
                    <div className="card-label">Free Shipping Orders</div>
                  </div>
                </div>
              </div>

              {shippingReport.by_customer_type && (
                <div className="chart-card">
                  <h3>Shipping by Customer Type</h3>
                  <div className="data-table-wrapper">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Customer Type</th>
                          <th className="number-cell">Orders</th>
                          <th className="number-cell">Shipping Charged</th>
                          <th className="number-cell">Discounts</th>
                          <th className="number-cell">Net Revenue</th>
                          <th className="number-cell">Avg per Order</th>
                        </tr>
                      </thead>
                      <tbody>
                        {shippingReport.by_customer_type.map((row: any) => (
                          <tr key={row.customer_type}>
                            <td><strong>{row.customer_type}</strong></td>
                            <td className="number-cell">{row.order_count}</td>
                            <td className="number-cell">{formatCurrency(row.total_charged)}</td>
                            <td className="number-cell">{formatCurrency(row.total_discounts)}</td>
                            <td className="number-cell">{formatCurrency(row.net_revenue)}</td>
                            <td className="number-cell">{formatCurrency(row.avg_per_order)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {shippingReport.free_shipping_detail && shippingReport.free_shipping_detail.length > 0 && (
                <div className="chart-card">
                  <h3>Free Shipping Analysis (50+ Five Star Bags)</h3>
                  <div className="data-table-wrapper">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Order #</th>
                          <th>Customer</th>
                          <th>Date</th>
                          <th className="number-cell">Bags</th>
                          <th className="number-cell">Shipping Cost Absorbed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {shippingReport.free_shipping_detail.map((row: any, i: number) => (
                          <tr key={i}>
                            <td style={{ fontFamily: 'monospace' }}>{row.order_number}</td>
                            <td>{row.customer_name}</td>
                            <td>{row.date}</td>
                            <td className="number-cell">{row.bag_count}</td>
                            <td className="number-cell">{formatCurrency(row.cost_absorbed)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="admin-reports-loading"><p>No data available.</p></div>
          )}
        </div>
      )}

      {/* Discounts Tab */}
      {activeTab === 'discounts' && (
        <div className="tab-content">
          {newTabLoading ? (
            <div className="admin-reports-loading"><div className="spinner"></div><p>Loading discount data...</p></div>
          ) : discountReport ? (
            <>
              <div className="summary-cards">
                <div className="summary-card">
                  <div className="card-icon avg-icon"><span>All</span></div>
                  <div className="card-content">
                    <div className="card-value">{formatCurrency(discountReport.summary?.grand_total_discounts ?? 0)}</div>
                    <div className="card-label">Total All Discounts</div>
                  </div>
                </div>
                <div className="summary-card">
                  <div className="card-icon orders-icon"><span>#</span></div>
                  <div className="card-content">
                    <div className="card-value">{discountReport.summary?.discounted_order_count ?? 0}</div>
                    <div className="card-label">Discounted Orders</div>
                  </div>
                </div>
                <div className="summary-card">
                  <div className="card-icon revenue-icon"><span>$</span></div>
                  <div className="card-content">
                    <div className="card-value">{formatCurrency(discountReport.summary?.average_discount ?? 0)}</div>
                    <div className="card-label">Average Discount</div>
                  </div>
                </div>
                <div className="summary-card">
                  <div className="card-icon lbs-icon"><span>Top</span></div>
                  <div className="card-content">
                    <div className="card-value" style={{ fontSize: '1rem' }}>{discountReport.summary?.top_category ?? 'N/A'}</div>
                    <div className="card-label">Top Category</div>
                  </div>
                </div>
              </div>

              {/* Category Breakdown */}
              {(discountReport.by_category ?? []).length > 0 && (
                <div className="chart-card">
                  <h3>Discount by Category</h3>
                  <div className="data-table-wrapper">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Category</th>
                          <th className="number-cell">Count</th>
                          <th className="number-cell">Total $</th>
                          <th className="number-cell">Avg $</th>
                          <th className="number-cell">% of Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(discountReport.by_category ?? []).map((row: any, i: number) => (
                          <tr key={i}>
                            <td><strong>{row.category}</strong></td>
                            <td className="number-cell">{row.count}</td>
                            <td className="number-cell">{formatCurrency(row.total)}</td>
                            <td className="number-cell">{formatCurrency(row.avg)}</td>
                            <td className="number-cell">{row.pct}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Monthly Trend */}
              {(discountReport.monthly_trend ?? []).length > 0 && (
                <div className="chart-card">
                  <h3>Monthly Discount Trend (Last 6 Months)</h3>
                  <div className="data-table-wrapper">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Month</th>
                          {(() => {
                            const allCats = new Set<string>();
                            (discountReport.monthly_trend ?? []).forEach((m: any) => {
                              Object.keys(m.categories || {}).forEach(c => allCats.add(c));
                            });
                            return Array.from(allCats).map(c => <th key={c} className="number-cell">{c}</th>);
                          })()}
                          <th className="number-cell"><strong>Total</strong></th>
                        </tr>
                      </thead>
                      <tbody>
                        {(discountReport.monthly_trend ?? []).map((row: any, i: number) => {
                          const allCats = new Set<string>();
                          (discountReport.monthly_trend ?? []).forEach((m: any) => {
                            Object.keys(m.categories || {}).forEach((c: string) => allCats.add(c));
                          });
                          return (
                            <tr key={i}>
                              <td>{row.month_label}</td>
                              {Array.from(allCats).map(c => (
                                <td key={c} className="number-cell">
                                  {row.categories[c] ? formatCurrency(row.categories[c]) : '\u2014'}
                                </td>
                              ))}
                              <td className="number-cell"><strong>{formatCurrency(row.total)}</strong></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Detail Table */}
              <div className="chart-card">
                <h3>Promo & Discount Detail</h3>
                <div className="data-table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Order #</th>
                        <th>Customer</th>
                        <th>Date</th>
                        <th>Category</th>
                        <th className="number-cell">Promo Items</th>
                        <th className="number-cell">Promo Value</th>
                        <th className="number-cell">Manual Discount</th>
                        <th className="number-cell">Shipping Discount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(discountReport.orders ?? []).map((row: any, i: number) => (
                        <tr key={i}>
                          <td style={{ fontFamily: 'monospace' }}>{row.order_number}</td>
                          <td>{row.customer_name}</td>
                          <td>{row.date}</td>
                          <td>{row.discount_category_name || row.discount_reason || '\u2014'}</td>
                          <td className="number-cell">{row.promo_item_count}</td>
                          <td className="number-cell">{formatCurrency(row.promo_value)}</td>
                          <td className="number-cell">{formatCurrency(row.manual_discount)}</td>
                          <td className="number-cell">{formatCurrency(row.shipping_discount)}</td>
                        </tr>
                      ))}
                      {(discountReport.orders ?? []).length === 0 && (
                        <tr><td colSpan={8} style={{ textAlign: 'center', color: '#999', padding: '2rem' }}>No discounted orders found in this date range.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : (
            <div className="admin-reports-loading"><p>No data available.</p></div>
          )}
        </div>
      )}

      {/* Walk-In Revenue Section — unmatched-visitor (walk-in) revenue,
          conversion rate, and top-spending walk-ins. Data comes from the
          UnmatchedVisitorTransaction ledger built by the Gmail receipt
          poller. Lazy-loaded once on first render of the page. */}
      <WalkInRevenueSection />
    </div>
  );
};

// ─── Walk-In Revenue Section ───────────────────────────────────────
// Lives at the bottom of the AdminReports page. Independent state +
// fetcher so it doesn't tangle with the existing date-range / customer
// filters at the top of the page (this report has its own date range).

interface WalkInRevenueResponse {
  range: { since: string; until: string };
  summary: {
    total_revenue: string;
    total_transactions: number;
    unique_visitors: number;
    converted_visitors: number;
    conversion_rate: number;
    avg_ticket: string;
  };
  top_visitors: Array<{
    id: number;
    name: string;
    visits: number;
    total_spend: string;
    last_visit: string | null;
    status: 'not_invited' | 'invited' | 'member';
  }>;
  monthly: Array<{ month: string; revenue: string; transactions: number }>;
}

const WalkInRevenueSection: React.FC = () => {
  const [data, setData] = useState<WalkInRevenueResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { default: api } = await import('../services/api');
        const res = await api.get('/admin/reports/walk-in-revenue/');
        if (!cancelled) setData(res.data);
      } catch (err: any) {
        if (!cancelled) setError(err?.response?.data?.error || 'Failed to load walk-in revenue');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return <div style={{ marginTop: 24, padding: 16, color: '#6B7280' }}>Loading walk-in revenue…</div>;
  if (error || !data) return <div style={{ marginTop: 24, padding: 16, color: '#B91C1C' }}>{error || 'No walk-in data'}</div>;

  const conversionPct = (data.summary.conversion_rate * 100).toFixed(1);
  const maxMonthlyRevenue = Math.max(
    1,
    ...data.monthly.map((m) => parseFloat(m.revenue) || 0)
  );

  const statusBadge = (status: string) => {
    if (status === 'member') return <span style={{ background: '#D1FAE5', color: '#065F46', padding: '2px 8px', borderRadius: 12, fontSize: 12, fontWeight: 600 }}>Member</span>;
    if (status === 'invited') return <span style={{ background: '#FEF3C7', color: '#92400E', padding: '2px 8px', borderRadius: 12, fontSize: 12, fontWeight: 600 }}>Invited</span>;
    return <span style={{ background: '#F3F4F6', color: '#6B7280', padding: '2px 8px', borderRadius: 12, fontSize: 12 }}>Not invited</span>;
  };

  return (
    <div style={{ marginTop: 32, padding: 16, background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 12 }}>
      <h2 style={{ marginTop: 0 }}>Walk-In Revenue</h2>
      <div style={{ color: '#6B7280', fontSize: 13, marginBottom: 16 }}>
        Range: {data.range.since} → {data.range.until} (last 30 days)
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 24 }}>
        <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: 12 }}>
          <div style={{ fontSize: 12, color: '#6B7280' }}>Total revenue</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#166534' }}>${data.summary.total_revenue}</div>
        </div>
        <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: 12 }}>
          <div style={{ fontSize: 12, color: '#6B7280' }}>Transactions</div>
          <div style={{ fontSize: 24, fontWeight: 700 }}>{data.summary.total_transactions}</div>
        </div>
        <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: 12 }}>
          <div style={{ fontSize: 12, color: '#6B7280' }}>Unique walk-ins</div>
          <div style={{ fontSize: 24, fontWeight: 700 }}>{data.summary.unique_visitors}</div>
        </div>
        <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: 12 }}>
          <div style={{ fontSize: 12, color: '#6B7280' }}>Conversion</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#2D5A27' }}>{conversionPct}%</div>
          <div style={{ fontSize: 11, color: '#6B7280' }}>
            {data.summary.converted_visitors} of {data.summary.unique_visitors} joined
          </div>
        </div>
        <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: 12 }}>
          <div style={{ fontSize: 12, color: '#6B7280' }}>Avg ticket</div>
          <div style={{ fontSize: 24, fontWeight: 700 }}>${data.summary.avg_ticket}</div>
        </div>
      </div>

      {data.monthly.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ marginBottom: 8 }}>Monthly trend</h3>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 120, padding: '8px 0' }}>
            {data.monthly.map((m) => {
              const rev = parseFloat(m.revenue) || 0;
              const heightPct = (rev / maxMonthlyRevenue) * 100;
              return (
                <div key={m.month} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <div style={{ fontSize: 11, color: '#6B7280' }}>${rev.toFixed(0)}</div>
                  <div
                    title={`${m.transactions} transactions`}
                    style={{
                      width: '100%',
                      height: `${Math.max(2, heightPct)}%`,
                      background: '#2D5A27',
                      borderRadius: '4px 4px 0 0',
                      minHeight: 2,
                    }}
                  />
                  <div style={{ fontSize: 11, color: '#374151' }}>{m.month}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <h3 style={{ marginBottom: 8 }}>Top 10 walk-in visitors (all-time)</h3>
      {data.top_visitors.length === 0 ? (
        <div style={{ color: '#6B7280' }}>No walk-in visitors recorded yet.</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#F9FAFB', textAlign: 'left' }}>
              <th style={{ padding: 8, borderBottom: '1px solid #E5E7EB' }}>Name</th>
              <th style={{ padding: 8, borderBottom: '1px solid #E5E7EB' }}>Visits</th>
              <th style={{ padding: 8, borderBottom: '1px solid #E5E7EB' }}>Total Spend</th>
              <th style={{ padding: 8, borderBottom: '1px solid #E5E7EB' }}>Last Visit</th>
              <th style={{ padding: 8, borderBottom: '1px solid #E5E7EB' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {data.top_visitors.map((v) => (
              <tr key={v.id}>
                <td style={{ padding: 8, borderBottom: '1px solid #E5E7EB' }}>
                  <a href={`/admin/bridge-club/walk-ins/${v.id}`} style={{ color: '#2D5A27', textDecoration: 'underline' }}>
                    {v.name}
                  </a>
                </td>
                <td style={{ padding: 8, borderBottom: '1px solid #E5E7EB' }}>{v.visits}</td>
                <td style={{ padding: 8, borderBottom: '1px solid #E5E7EB' }}>${v.total_spend}</td>
                <td style={{ padding: 8, borderBottom: '1px solid #E5E7EB' }}>{v.last_visit || '—'}</td>
                <td style={{ padding: 8, borderBottom: '1px solid #E5E7EB' }}>{statusBadge(v.status)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

// Helper function to get animal type colors
function getAnimalColor(index: number): string {
  const colors = [
    '#508590', // Teal
    '#E73C3E', // Red
    '#C3AD8B', // Gold
    '#3F2021', // Brown
    '#6B8E23', // Olive
    '#4682B4', // Steel Blue
    '#CD853F', // Peru
    '#708090', // Slate Gray
  ];
  return colors[index % colors.length];
}

export default AdminReports;
